import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Container, Row, Col, Card, Button, Modal, Form, ListGroup, Tabs, Tab, Alert, Spinner, InputGroup } from 'react-bootstrap';
import projectService from '../services/projectService';
import fileService from '../services/fileService';
import folderService from '../services/folderService';
import doorService from '../services/doorService';
import { supabase } from '../config/supabase';
import { 
  FaFolderPlus, FaUpload, FaTrash, FaEye, FaDoorClosed, FaArrowLeft, FaDownload, FaEdit,
  FaFilePdf, FaCheck, FaFileImage, FaFileExcel, FaFile, FaCopy, FaExclamationTriangle, FaInfoCircle, FaDraftingCompass
} from 'react-icons/fa';
import PrivateRoute from '../components/PrivateRoute';
import RenderWindow from '../components/RenderWindow';
import PDFViewer from '../components/PDFViewer';
import FileViewer from '../components/FileViewer';
import DrawingGeneratorModal from '../components/DrawingGeneratorModal';
import { toast } from 'react-toastify';

const ProjectDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [project, setProject] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  
  // Modal states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showPdfUploadModal, setShowPdfUploadModal] = useState(false);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [showDoorForm, setShowDoorForm] = useState(false);
  const [showFileViewerModal, setShowFileViewerModal] = useState(false);
  const [selectedFileForViewing, setSelectedFileForViewing] = useState(null);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedDoorForDetails, setSelectedDoorForDetails] = useState(null);
  const [doorToCopy, setDoorToCopy] = useState(null);
  const [showDrawingGeneratorModal, setShowDrawingGeneratorModal] = useState(false);
  const [selectedDoorForDrawing, setSelectedDoorForDrawing] = useState(null);
  const [copyDoorName, setCopyDoorName] = useState('');
  const [copyToProjectId, setCopyToProjectId] = useState('');
  const [availableProjects, setAvailableProjects] = useState([]);
  const [showSimilarDoorsModal, setShowSimilarDoorsModal] = useState(false);
  const [similarDoors, setSimilarDoors] = useState([]);
  const [loadingSimilarDoors, setLoadingSimilarDoors] = useState(false);
  
  // Project editing state
  const [showProjectEditModal, setShowProjectEditModal] = useState(false);
  const [editProjectData, setEditProjectData] = useState({
    name: '',
    description: '',
    status: 'New',
    dueDate: '',
    clientId: null
  });
  const [clients, setClients] = useState([]);
  
  // Selected folder for file upload
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [folderFiles, setFolderFiles] = useState([]);
  
  // File upload state
  const fileInputRef = useRef();
  const [isDragging, setIsDragging] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const dropAreaRef = useRef();

  // Folder state
  const [folders, setFolders] = useState([]);
  const [folderName, setFolderName] = useState('');
  const [selectedFolder, setSelectedFolder] = useState(null);
  
  // Door metadata state
  const [doorData, setDoorData] = useState({
    width: '988',
    height: '2078',
    doorName: '60O',
    hasGlass: false,
    glassStyle: '00',
    glassWidth: '0',
    glassHeight: '0',
    grooveDesign: '000',
    hingeCount: '2',
    hingeType: '2',
    lockType: '565 analoge',
    strikerType: 'none',
    framePaint: 'FP3',
    amount: 1,
    waternose: false,
    thresholdType: 'Alu w holes',
    kickplate: false,
    surfaceFinish: 'R',
    customFinish: '',
    colorCode: '7016',
    handedness: 'L',
    doorStatus: 'A',
    specialFeature: false
  });
  
  // Door options - matching door code generator standards
  const doorTypes = [
    { value: '60O', label: '60mm Outswing' },
    { value: '60I', label: '60mm Inswing' },
    { value: 'MER', label: 'Meranti' },
    { value: 'COT', label: 'Cottage' },
    { value: '18D', label: '18 Degree' }
  ];
  const hingeCountOptions = ['2', '3', '4'];
  const hingeTypes = [
    { value: '2', label: 'H2 - Standard' },
    { value: '5', label: 'H5 - Tectus' }
  ];
  const lockTypes = [
    { value: 'none', label: 'None' },
    { value: '565 analoge', label: '565 analoge' },
    { value: '8765 analoge', label: '8765 analoge' },
    { value: '2150-3PL analoge', label: '2150-3PL analoge' },
    { value: 'ASSA 8765', label: 'ASSA 8765' }
  ];
  const strikerTypes = [
    { value: 'none', label: 'None' },
    { value: '2860-1ZN', label: '2860-1ZN' },
    { value: 'Scanlock 4690', label: 'Scanlock 4690' },
    { value: '3PL strike', label: '3PL strike' },
    { value: '1887-2', label: '1887-2' },
    { value: '1220/9 adjust', label: '1220/9 adjust' },
    { value: 'ASSA 1887-2', label: 'ASSA 1887-2' }
  ];
  const framePaintOptions = [
    { value: 'none', label: 'None' },
    { value: 'FP-3', label: 'FP-3 (Painted visible sides)' },
    { value: 'FP-4', label: 'FP-4 (Painted all sides)' }
  ];
  const thresholdTypes = [
    { value: 'Pine', label: 'Pine' },
    { value: 'Oak', label: 'Oak' },
    { value: 'ACCOYA', label: 'ACCOYA' },
    { value: 'Alu w holes', label: 'Alu w holes' },
    { value: 'Alu-no holes', label: 'Alu-no holes' }
  ];
  const surfaceFinishes = [
    { value: 'R', label: 'RAL' },
    { value: 'N', label: 'NCS' },
    { value: 'ST', label: 'Stain (Peits)' }
  ];
  const handednessOptions = [
    { value: 'L', label: 'L - Left Hand' },
    { value: 'R', label: 'R - Right Hand' }
  ];
  const doorStatusOptions = [
    { value: 'A', label: 'A - Active (Default)' },
    { value: 'P', label: 'P - Passive' }
  ];

  // Function to determine badge color based on status
  const getBadgeColor = (status) => {
    switch (status) {
      case 'Draft':
        return 'bg-info';
      case 'Released for Production':
        return 'bg-success';
      case 'In Production':
        return 'bg-secondary';
      case 'Completed':
        return 'bg-success';
      case 'Delivered':
        return 'bg-dark';
      default:
        return 'bg-light text-dark';
    }
  };

  // Helper function to get door type label
  const getDoorTypeLabel = (value) => {
    const doorType = doorTypes.find(type => type.value === value);
    return doorType ? doorType.label : value;
  };

  // Helper function to get threshold type label
  const getThresholdTypeLabel = (value) => {
    const thresholdType = thresholdTypes.find(type => type.value === value);
    return thresholdType ? thresholdType.label : 'No threshold';
  };

  // Helper function to get surface finish label
  const getSurfaceFinishLabel = (value) => {
    const finish = surfaceFinishes.find(finish => finish.value === value);
    return finish ? finish.label : value;
  };

  // Helper function to get handedness label
  const getHandednessLabel = (value) => {
    const hand = handednessOptions.find(option => option.value === value);
    return hand ? hand.label : value;
  };

  // Helper function to get hinge type label
  const getHingeTypeLabel = (value) => {
    const hinge = hingeTypes.find(type => type.value === value);
    return hinge ? hinge.label : value;
  };

  // Helper function to get lock type label
  const getLockTypeLabel = (value) => {
    const lock = lockTypes.find(type => type.value === value);
    return lock ? lock.label : value;
  };

  // Helper function to get door status label
  const getDoorStatusLabel = (value) => {
    const status = doorStatusOptions.find(option => option.value === value);
    return status ? status.label : value;
  };

  // Helper function to get door image path
  const getDoorImagePath = (doorType) => {
    const doorImageMap = {
      'door_18deg': '/18Deg.png',
      'door_cottage': '/Cottage.png',
      'door_60mm_inward': '/60mmInswing.png',
      'door_meranti': '/Meranti.png',
      'door_60mm': '/60mm.png'
    };
    return doorImageMap[doorType] || '/60mm.png'; // Default to 60mm if not found
  };

  // Helper function to get door background color based on finish and color code
  const getDoorBackgroundColor = (doorMetadata) => {
    if (!doorMetadata) return '#ffffff'; // Default white
    
    const { surfaceFinish, colorCode, colorSystem } = doorMetadata;
    
    if (!colorCode) return '#ffffff'; // Default white
    
    // Check both surfaceFinish and colorSystem fields
    const finishType = surfaceFinish || colorSystem;
    
    if (finishType === 'RAL' || finishType === 'NCS') {
      return getColorPreview(finishType, colorCode);
    }
    
    return '#ffffff'; // Default white for other finishes
  };

  // Helper function to get color preview
  const getColorPreview = (colorSystem, colorCode) => {
    if (!colorCode) return '#ffffff';
    
    // Basic RAL color mapping (you can expand this)
    const ralColors = {
      '1000': '#BEBD7F', '1001': '#C2B078', '1002': '#C6A664', '1003': '#E5BE01',
      '1004': '#CDA434', '1005': '#A98307', '1006': '#E4B429', '1007': '#DC9D00',
      '1011': '#8A6642', '1012': '#C7B446', '1013': '#EAE6CA', '1014': '#E1CC4F',
      '1015': '#E6D690', '1016': '#EDFF21', '1017': '#F5D033', '1018': '#F8F32B',
      '1019': '#9E9764', '1020': '#999950', '1021': '#F3DA0B', '1023': '#FAD201',
      '1024': '#AEA04B', '1027': '#9D9101', '1028': '#F4A900', '1032': '#D6AE01',
      '1033': '#F3A505', '1034': '#EFA94A', '1035': '#6A5D4D', '1036': '#705335',
      '1037': '#F39800', '2000': '#ED760E', '2001': '#C93C20', '2002': '#CB2821',
      '2003': '#FF7514', '2004': '#F44611', '2008': '#F75E25', '2009': '#F54021',
      '2010': '#D84B20', '2011': '#EC7C26', '2012': '#E55137', '3000': '#AF2B1E',
      '3001': '#A52019', '3002': '#A2231D', '3003': '#9B111E', '3004': '#75151E',
      '3005': '#5E2129', '3007': '#412227', '3009': '#642424', '3011': '#781F19',
      '3012': '#C1876B', '3013': '#A12312', '3014': '#D36E70', '3015': '#EA899A',
      '3016': '#B32821', '3017': '#E63244', '3018': '#D53032', '3020': '#CC0605',
      '3022': '#D95030', '3024': '#F80000', '3026': '#FE0000', '3027': '#C51D34',
      '3028': '#CB3234', '3031': '#B32428', '3032': '#721422', '3033': '#B44C43',
      '4001': '#6D3F5B', '4002': '#922B3E', '4003': '#DE4C8A', '4004': '#641C34',
      '4005': '#6C4675', '4006': '#A03472', '4007': '#4A192C', '4008': '#924B75',
      '4009': '#A18594', '4010': '#CF3476', '4011': '#8673A1', '4012': '#6C6874',
      '5000': '#354D73', '5001': '#1F3438', '5002': '#20214F', '5003': '#1D1E33',
      '5004': '#18171C', '5005': '#1E2460', '5007': '#3E5F8A', '5008': '#26252D',
      '5009': '#025669', '5010': '#0E294B', '5011': '#231A24', '5012': '#3B83BD',
      '5013': '#1E213D', '5014': '#606E8C', '5015': '#2271B3', '5017': '#063971',
      '5018': '#3F888F', '5019': '#1B5583', '5020': '#1D334A', '5021': '#256D7B',
      '5022': '#252850', '5023': '#49678D', '5024': '#5D9B9B', '6000': '#316650',
      '6001': '#287233', '6002': '#2D5016', '6003': '#424632', '6004': '#1F3A3D',
      '6005': '#2F4F4F', '6006': '#3E3B32', '6007': '#343B29', '6008': '#39352A',
      '6009': '#31372B', '6010': '#35682D', '6011': '#587246', '6012': '#343E40',
      '6013': '#6C7156', '6014': '#47402E', '6015': '#3B3C36', '6016': '#1E5945',
      '6017': '#4C9141', '6018': '#57A639', '6019': '#BDECB6', '6020': '#2E3A23',
      '6021': '#89AC76', '6022': '#25221B', '6024': '#308446', '6025': '#3D642D',
      '6026': '#015D52', '6027': '#84C3CE', '6028': '#2C5545', '6029': '#20603D',
      '6032': '#317F43', '6033': '#497E76', '6034': '#7FB5B5', '7000': '#78858B',
      '7001': '#8A9597', '7002': '#7E7B52', '7003': '#6C7059', '7004': '#969992',
      '7005': '#646B63', '7006': '#6D6552', '7008': '#6A5F31', '7009': '#4D5645',
      '7010': '#4C514A', '7011': '#434B4D', '7012': '#4E5754', '7013': '#464531',
      '7015': '#434750', '7016': '#293133', '7021': '#23282B', '7022': '#332F2C',
      '7023': '#686C5E', '7024': '#474A51', '7026': '#2F353B', '7030': '#8B8C7A',
      '7031': '#474B4E', '7032': '#B8B799', '7033': '#7D8471', '7034': '#8F8B66',
      '7035': '#D7D3D0', '7036': '#7F7679', '7037': '#7D7F7D', '7038': '#B5B8B1',
      '7039': '#6C6960', '7040': '#9DA1AA', '7042': '#8D948D', '7043': '#4E5452',
      '7044': '#CAC4B0', '7045': '#909090', '7046': '#82898F', '7047': '#D0D0D0',
      '7048': '#898176', '8000': '#826C34', '8001': '#955F20', '8002': '#6C3B2A',
      '8003': '#734222', '8004': '#8E402A', '8007': '#59351F', '8008': '#6F4F28',
      '8011': '#5B3A29', '8012': '#592321', '8014': '#382C1E', '8015': '#633A34',
      '8016': '#4C2F27', '8017': '#45322E', '8019': '#403A3A', '8022': '#212121',
      '8023': '#A65E2E', '8024': '#79553D', '8025': '#755C48', '8028': '#4E3B31',
      '9001': '#FDF4E3', '9002': '#E7EBDA', '9003': '#F4F4F4', '9004': '#282828',
      '9005': '#0A0A0A', '9006': '#A5A5A5', '9007': '#8F8F8F', '9010': '#FFFFFF',
      '9011': '#1C1C1C', '9016': '#F6F6F6', '9017': '#1E1E1E', '9018': '#D7D7D7'
    };
    
    // Basic NCS color mapping
    const ncsColors = {
      '0300-N': '#F8F8F8', '0500-N': '#F0F0F0', '1000-N': '#E8E8E8', '1500-N': '#E0E0E0',
      '2000-N': '#D8D8D8', '2500-N': '#D0D0D0', '3000-N': '#C8C8C8', '3500-N': '#C0C0C0',
      '4000-N': '#B8B8B8', '4500-N': '#B0B0B0', '5000-N': '#A8A8A8', '5500-N': '#A0A0A0',
      '6000-N': '#989898', '6500-N': '#909090', '7000-N': '#888888', '7500-N': '#808080',
      '8000-N': '#787878', '8500-N': '#707070', '9000-N': '#686868', '0502-Y': '#F5F2E8',
      '1005-Y': '#EDE6D0', '1510-Y': '#E5DAB8', '2010-Y': '#DDCEA0', '2020-Y': '#D5C288',
      '3020-Y': '#CDB670', '4020-Y': '#C5AA58', '5020-Y': '#BD9E40', '6020-Y': '#B59228',
      '1015-Y10R': '#E8D5B0', '2010-Y10R': '#E0C898', '3010-Y10R': '#D8BB80', '4010-Y10R': '#D0AE68',
      '5010-Y10R': '#C8A150', '6010-Y10R': '#C09438', '1020-Y20R': '#E5CFA8', '2020-Y20R': '#DDC290',
      '3020-Y20R': '#D5B578', '4020-Y20R': '#CDA860', '5020-Y20R': '#C59B48', '6020-Y20R': '#BD8E30',
      '1030-Y30R': '#E2C9A0', '2030-Y30R': '#DABC88', '3030-Y30R': '#D2AF70', '4030-Y30R': '#CAA258',
      '5030-Y30R': '#C29540', '6030-Y30R': '#BA8828', '1040-Y40R': '#DFC398', '2040-Y40R': '#D7B680',
      '3040-Y40R': '#CFA968', '4040-Y40R': '#C79C50', '5040-Y40R': '#BF8F38', '6040-Y40R': '#B78220',
      '1050-Y50R': '#DCBD90', '2050-Y50R': '#D4B078', '3050-Y50R': '#CCA360', '4050-Y50R': '#C49648',
      '5050-Y50R': '#BC8930', '6050-Y50R': '#B47C18', '1060-Y60R': '#D9B788', '2060-Y60R': '#D1AA70',
      '3060-Y60R': '#C99D58', '4060-Y60R': '#C19040', '5060-Y60R': '#B98328', '6060-Y60R': '#B17610',
      '1070-Y70R': '#D6B180', '2070-Y70R': '#CEA468', '3070-Y70R': '#C69750', '4070-Y70R': '#BE8A38',
      '5070-Y70R': '#B67D20', '6070-Y70R': '#AE7008', '1080-Y80R': '#D3AB78', '2080-Y80R': '#CB9E60',
      '3080-Y80R': '#C39148', '4080-Y80R': '#BB8430', '5080-Y80R': '#B37718', '6080-Y80R': '#AB6A00',
      '1005-R90B': '#E8E6F0', '2005-R90B': '#E0DDE8', '3005-R90B': '#D8D4E0', '4005-R90B': '#D0CBD8',
      '5005-R90B': '#C8C2D0', '6005-R90B': '#C0B9C8', '1010-R90B': '#E5E2F0', '2010-R90B': '#DDDAE8',
      '3010-R90B': '#D5D1E0', '4010-R90B': '#CDC8D8', '5010-R90B': '#C5BFD0', '6010-R90B': '#BDB6C8',
      '1015-R80B': '#E2DEF0', '2015-R80B': '#DAD5E8', '3015-R80B': '#D2CCE0', '4015-R80B': '#CAC3D8',
      '5015-R80B': '#C2BAD0', '6015-R80B': '#BAB1C8', '1020-R70B': '#DFDAF0', '2020-R70B': '#D7D1E8',
      '3020-R70B': '#CFC8E0', '4020-R70B': '#C7BFD8', '5020-R70B': '#BFB6D0', '6020-R70B': '#B7ADC8',
      '1030-R60B': '#DCD6F0', '2030-R60B': '#D4CDE8', '3030-R60B': '#CCC4E0', '4030-R60B': '#C4BBD8',
      '5030-R60B': '#BCB2D0', '6030-R60B': '#B4A9C8', '1040-R50B': '#D9D2F0', '2040-R50B': '#D1C9E8',
      '3040-R50B': '#C9C0E0', '4040-R50B': '#C1B7D8', '5040-R50B': '#B9AED0', '6040-R50B': '#B1A5C8',
      '1005-G90Y': '#E8F0E6', '2005-G90Y': '#E0E8DE', '3005-G90Y': '#D8E0D6', '4005-G90Y': '#D0D8CE',
      '5005-G90Y': '#C8D0C6', '6005-G90Y': '#C0C8BE', '1010-G90Y': '#E5F0E2', '2010-G90Y': '#DDE8DA',
      '3010-G90Y': '#D5E0D2', '4010-G90Y': '#CDD8CA', '5010-G90Y': '#C5D0C2', '6010-G90Y': '#BDC8BA',
      '1020-G80Y': '#DFF0DE', '2020-G80Y': '#D7E8D6', '3020-G80Y': '#CFE0CE', '4020-G80Y': '#C7D8C6',
      '5020-G80Y': '#BFD0BE', '6020-G80Y': '#B7C8B6', '1030-G70Y': '#D9F0DA', '2030-G70Y': '#D1E8D2',
      '3030-G70Y': '#C9E0CA', '4030-G70Y': '#C1D8C2', '5030-G70Y': '#B9D0BA', '6030-G70Y': '#B1C8B2'
    };
    
    if (colorSystem === 'RAL' && ralColors[colorCode]) {
      return ralColors[colorCode];
    }
    
    if (colorSystem === 'NCS' && ncsColors[colorCode]) {
      return ncsColors[colorCode];
    }
    
    // For unknown colors, return a default gray
    return '#CCCCCC';
  };

  // Fetch project, files, and folders
  const fetchProjectDetails = async () => {
    try {
      setLoading(true);
      const projectData = await projectService.getProject(id);
      setProject(projectData);
      
      // Set edit project data
      setEditProjectData({
        name: projectData.name || '',
        description: projectData.description || '',
        status: projectData.status || 'New',
        dueDate: projectData.due_date ? projectData.due_date.split('T')[0] : ''
      });
      
      // Extract files and folders from project data
      const allFiles = [];
      const allFolders = [];
      
      // Add root-level files (files directly attached to project)
      if (projectData.files) {
        allFiles.push(...projectData.files);
      }
      
      // Add folder files and map door metadata
      if (projectData.folders) {
        projectData.folders.forEach(folder => {
          // Map door_metadata to doorMetadata for UI compatibility
          if (folder.door_metadata && folder.door_metadata.length > 0) {
            const dbMetadata = folder.door_metadata[0]; // Get first metadata record
            folder.doorMetadata = {
              id: dbMetadata.id,
              itemNr: dbMetadata.item_nr,
              barcodeUrl: dbMetadata.barcode_url,
              width: dbMetadata.width,
              height: dbMetadata.height,
              doorName: dbMetadata.door_name,
              hasGlass: dbMetadata.has_glass,
              glassStyle: dbMetadata.glass_style,
              hingeType: dbMetadata.hinge_type,
              hingeCount: dbMetadata.hinge_count,
              lockType: dbMetadata.lock_type,
              amount: dbMetadata.amount,
              waternose: dbMetadata.waternose,
              thresholdType: dbMetadata.threshold_type,
              kickplate: dbMetadata.kickplate,
              surfaceFinish: dbMetadata.surface_finish,
              customFinish: dbMetadata.custom_finish,
              colorCode: dbMetadata.color_code,
              paintType: dbMetadata.paint_type,
              paintCode: dbMetadata.paint_code,
              framePaint: dbMetadata.frame_paint,
              handedness: dbMetadata.handedness
            };
          }
          
          allFolders.push(folder);
          if (folder.files) {
            allFiles.push(...folder.files);
          }
        });
      }
      
      // Only show root-level files (files without a folder_id)
      const rootFiles = allFiles.filter(file => !file.folder_id) || [];
      console.log('📁 ProjectDetails: Files loaded:', {
        totalFiles: allFiles.length,
        rootFiles: rootFiles.length,
        folders: allFolders.length,
        foldersWithMetadata: allFolders.filter(f => f.doorMetadata).length
      });
      
      setFiles(rootFiles);
      setFolders(allFolders || []);
      
      setError('');
    } catch (err) {
      setError('Failed to fetch project details. Please try again.');
      console.error('Error fetching project details:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch files for a specific folder
  const fetchFolderFiles = async (folderId) => {
    try {
      setLoading(true);
      const { data: files, error } = await supabase
        .from('files')
        .select('*')
        .eq('project_id', id)
        .eq('folder_id', folderId);
      
      if (error) throw error;
      setFolderFiles(files || []);
    } catch (err) {
      toast.error('Failed to fetch folder files');
      console.error('Error fetching folder files:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjectDetails();
  }, [id]);

  // Prevent default drag behavior on the entire page
  useEffect(() => {
    const preventDefaults = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handlePageDragOver = (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'none';
    };

    const handlePageDrop = (e) => {
      e.preventDefault();
      return false;
    };

    // Add event listeners to prevent default drag behavior
    document.addEventListener('dragenter', preventDefaults, false);
    document.addEventListener('dragover', handlePageDragOver, false);
    document.addEventListener('drop', handlePageDrop, false);

    return () => {
      document.removeEventListener('dragenter', preventDefaults, false);
      document.removeEventListener('dragover', handlePageDragOver, false);
      document.removeEventListener('drop', handlePageDrop, false);
    };
  }, []);

  // Drag & Drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Only set isDragOver to false if we're leaving the drop zone entirely
    // Check if the related target is outside the drop zone
    const dropZone = e.currentTarget;
    const relatedTarget = e.relatedTarget;
    
    if (!dropZone.contains(relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    // Validate files before uploading
    const supportedExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'xlsx', 'xls', 'ods', 'msg', 'eml'];
    const invalidFiles = files.filter(file => {
      const extension = file.name.split('.').pop().toLowerCase();
      return !supportedExtensions.includes(extension);
    });

    if (invalidFiles.length > 0) {
      toast.error(`Unsupported file types: ${invalidFiles.map(f => f.name).join(', ')}`);
      return;
    }

    // Upload each file
    setUploading(true);
    let successCount = 0;
    let errorCount = 0;

    for (const file of files) {
      try {
        // Upload to current folder if viewing a folder, otherwise project root
        await fileService.uploadFile(file, id, currentFolder?.id || null);
        successCount++;
        toast.success(`${file.name} uploaded successfully`);
      } catch (err) {
        errorCount++;
        toast.error(`Failed to upload ${file.name}: ${err.message}`);
        console.error('Error uploading file:', err);
      }
    }
    
    setUploading(false);
    
    // Show summary if multiple files
    if (files.length > 1) {
      toast.info(`Upload complete: ${successCount} successful, ${errorCount} failed`);
    }
    
    // Refresh appropriate view
    if (currentFolder) {
      fetchFolderFiles(currentFolder.id);
    } else {
      fetchProjectDetails();
    }
  };

  // Handle file input change
  const handleFileInputChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    
    // Upload each file
    for (const file of files) {
      try {
        // Upload to current folder if viewing a folder, otherwise project root
        await fileService.uploadFile(file, id, currentFolder?.id || null);
        toast.success(`${file.name} uploaded successfully`);
      } catch (err) {
        toast.error(`Failed to upload ${file.name}: ${err.message}`);
        console.error('Error uploading file:', err);
      }
    }
    
    setUploading(false);
    e.target.value = ''; // Reset input
    
    // Refresh appropriate view
    if (currentFolder) {
      fetchFolderFiles(currentFolder.id);
    } else {
      fetchProjectDetails();
    }
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    e.preventDefault();
    
    let file = selectedFile;
    
    // If no file selected via drag & drop, check file input
    if (!file) {
      const fileInput = fileInputRef.current;
      
      if (!fileInput || !fileInput.files || fileInput.files.length === 0) {
        toast.error('Please select a file to upload (PDF, JPG, PNG, GIF, or Excel)');
        return;
      }
      
      file = fileInput.files[0];
    }
    
    // Get file extension
    const fileExtension = file.name.split('.').pop().toLowerCase();
    
    // If uploading to a folder (door), only allow PDFs
    if (selectedFolderId) {
      if (fileExtension !== 'pdf') {
        toast.error('Door drawings must be PDF files');
        return;
      }
    } else {
      // For Files tab, allow all supported types
      const supportedExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'xlsx', 'xls', 'ods', 'msg', 'eml'];
      
      if (!supportedExtensions.includes(fileExtension)) {
        toast.error('Please upload a supported file type (PDF, JPG, PNG, GIF, Excel, OpenDocument, or Outlook email)');
        return;
      }
    }
    
    // Create form data for file upload
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      setUploading(true);
      
      await fileService.uploadFile(file, id, selectedFolderId);
      toast.success('File uploaded successfully');
      setShowUploadModal(false);
      
      // Refresh project details
      fetchProjectDetails();
    } catch (err) {
      toast.error(err.message || 'Failed to upload file');
      console.error('Error uploading file:', err);
    } finally {
      setUploading(false);
      fileInputRef.current.value = null; // Reset file input
      setSelectedFile(null); // Clear selected file
      // Reset selected folder for upload if it was set
      setSelectedFolderId(null);
    }
  };

  
  // Handle file selection from input
  const handleFileSelection = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      
      // Check if file is a PDF
      if (file.type !== 'application/pdf') {
        toast.error('Only PDF files are allowed');
        e.target.value = null;
        return;
      }
      
      setSelectedFile(file);
    } else {
      setSelectedFile(null);
    }
  };

  // Handle file selection for upload modal
  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      
      // Check if file is a PDF
      if (file.type !== 'application/pdf') {
        toast.error('Only PDF files are allowed');
        e.target.value = null;
        return;
      }
      
      setSelectedFile(file);
    } else {
      setSelectedFile(null);
    }
  };

  // Handle upload for upload modal
  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file to upload');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      setUploading(true);
      
      await fileService.uploadFile(selectedFile, id, selectedFolderId);
      toast.success('File uploaded successfully');
      setShowPdfUploadModal(false);
      setSelectedFile(null);
      
      // Refresh project details
      fetchProjectDetails();
    } catch (err) {
      toast.error(err.message || 'Failed to upload file');
      console.error('Error uploading file:', err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
      setSelectedFolderId(null);
    }
  };

  // Fetch clients for dropdown
  const fetchClients = async () => {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('id, name, company_name')
        .order('name');
      
      if (error) throw error;
      setClients(data || []);
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  };

  // Handle project editing
  const handleEditProject = async (e) => {
    e.preventDefault();
    
    try {
      console.log('Updating project with data:', {
        name: editProjectData.name,
        description: editProjectData.description,
        status: editProjectData.status,
        dueDate: editProjectData.dueDate,
        clientId: editProjectData.clientId
      });
      
      const updatedProject = await projectService.updateProject(id, {
        name: editProjectData.name,
        description: editProjectData.description,
        status: editProjectData.status,
        dueDate: editProjectData.dueDate,
        clientId: editProjectData.clientId
      });
      
      console.log('Updated project returned:', updatedProject);
      
      // Update project state immediately with the returned data
      setProject(prevProject => ({
        ...prevProject,
        ...updatedProject
      }));
      
      toast.success('Project updated successfully');
      setShowProjectEditModal(false);
      
      // Refresh project details to get full data
      await fetchProjectDetails();
    } catch (err) {
      toast.error(err.message || 'Failed to update project');
      console.error('Update project error:', err);
    }
  };

  // Function to check if a folder has PDF drawings
  const hasPdfDrawings = (folder) => {
    if (!folder || !folder.files || folder.files.length === 0) {
      return false;
    }
    
    // Check if any file in the folder is a PDF
    return folder.files.some(file => {
      if (!file) return false;
      
      return file.fileType === 'application/pdf' || 
             file.fileType === 'pdf' || 
             file.file_type === 'application/pdf' ||
             (file.originalName && file.originalName.toLowerCase().endsWith('.pdf')) ||
             (file.original_name && file.original_name.toLowerCase().endsWith('.pdf')) ||
             (file.filename && file.filename.toLowerCase().endsWith('.pdf'));
    });
  };

  // Handle door creation with metadata in one step
  const handleCreateFolder = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!folderName) {
      toast.error('Please enter a door number/ID');
      return;
    }
    
    if (!doorData.width || !doorData.height) {
      toast.error('Please enter door dimensions');
      return;
    }
    
    try {
      console.log('Creating door with data:', { folderName, doorData });
      
      // Step 1: Create the folder first
      const folderData = await folderService.createFolder(id, folderName, 'door');
      console.log('Folder created:', folderData);
      
      // Step 2: If folder creation was successful, add the door metadata
      if (folderData && folderData.id) {
        console.log('🚪 Creating door metadata for folder:', folderData.id);
        console.log('🚪 Door data being saved:', doorData);
        
        const doorMetadata = await doorService.saveDoorMetadata(folderData.id, doorData);
        console.log('✅ Door metadata created:', doorMetadata);
        console.log('📦 Item number:', doorMetadata?.itemNr);
        console.log('🔗 Barcode URL:', doorMetadata?.barcodeUrl);
        
        if (doorMetadata && doorMetadata.barcodeUrl) {
          toast.success(`Door created with barcode! Item: ${doorMetadata.itemNr}`);
        } else if (doorMetadata && doorMetadata.itemNr) {
          toast.warning(`Door created with item number ${doorMetadata.itemNr}, but barcode generation failed`);
        } else {
          toast.warning('Door created but item number/barcode generation failed');
        }
      } else {
        toast.error('Door folder created but metadata could not be added');
      }
      
      // Reset form data to defaults
      setFolderName('');
      setDoorData({
        width: '988',
        height: '2078',
        doorName: '60O',
        hasGlass: false,
        glassStyle: '00',
        glassWidth: '0',
        glassHeight: '0',
        grooveDesign: '000',
        hingeCount: '2',
        hingeType: '2',
        lockType: '01',
        amount: 1,
        waternose: false,
        thresholdType: 'TA',
        kickplate: false,
        surfaceFinish: 'R',
        customFinish: '',
        colorCode: '7016',
        handedness: 'L',
        doorStatus: 'A',
        specialFeature: false
      });
      setShowFolderModal(false);
      
      // Refresh folders to show the new door with barcode
      await fetchProjectDetails();
    } catch (err) {
      toast.error(err.message || 'Failed to create door');
      console.error('Error creating door:', err);
    }
  };
  
  // Handle door data submission
  const handleDoorDataSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedFolder) {
      toast.error('No folder selected');
      return;
    }
    
    try {
      const updatedMetadata = await doorService.saveDoorMetadata(selectedFolder.id, doorData);
      toast.success('Door data saved successfully');
      setShowDoorForm(false);
      
      // If we're currently viewing this folder, update it immediately
      if (currentFolder && currentFolder.id === selectedFolder.id) {
        setCurrentFolder({
          ...currentFolder,
          doorMetadata: updatedMetadata
        });
      }
      
      setSelectedFolder(null);
      
      // Reset form for new door metadata
      setDoorData({
        width: '',
        height: '',
        doorName: 'door_60mm',
        hasGlass: false,
        hingeType: '2',
        lockType: 'Assa',
        amount: 1,
        waternose: false,
        thresholdType: '',
        kickplate: false,
        surfaceFinish: 'Untreated',
        customFinish: '',
        colorCode: '',
        handedness: 'Left'
      });
      
      // Refresh folders with updated data
      fetchProjectDetails();
    } catch (err) {
      toast.error(err.message || 'Failed to save door data');
      console.error('Error saving door data:', err);
    }
  };
  
  // Handle selecting a folder to edit metadata
  const handleFolderSelect = (folder) => {
    setSelectedFolder(folder);
    
    // If door metadata already exists, populate the form
    if (folder.doorMetadata) {
      setDoorData({
        width: folder.doorMetadata.width,
        height: folder.doorMetadata.height,
        doorName: folder.doorMetadata.doorName,
        hasGlass: folder.doorMetadata.hasGlass,
        glassStyle: folder.doorMetadata.glassStyle || '00',
        glassWidth: folder.doorMetadata.glassWidth || '0',
        glassHeight: folder.doorMetadata.glassHeight || '0',
        grooveDesign: folder.doorMetadata.grooveDesign || '000',
        hingeCount: folder.doorMetadata.hingeCount || '2',
        hingeType: folder.doorMetadata.hingeType,
        lockType: folder.doorMetadata.lockType || '565 analoge',
        strikerType: folder.doorMetadata.strikerType || 'none',
        framePaint: folder.doorMetadata.framePaint || 'FP3',
        amount: folder.doorMetadata.amount,
        waternose: folder.doorMetadata.waternose || false,
        thresholdType: folder.doorMetadata.thresholdType || 'Alu w holes',
        kickplate: folder.doorMetadata.kickplate || false,
        surfaceFinish: folder.doorMetadata.surfaceFinish || 'Untreated',
        customFinish: folder.doorMetadata.customFinish || '',
        colorCode: folder.doorMetadata.colorCode || '',
        handedness: folder.doorMetadata.handedness || 'Left',
        doorStatus: folder.doorMetadata.doorStatus || 'A',
        specialFeature: folder.doorMetadata.specialFeature || false
      });
    }
    
    setShowDoorForm(true);
  };
  
  // Handle opening a folder to view/upload files
  const handleOpenFolder = (folder) => {
    setCurrentFolder(folder);
    setSelectedFolderId(folder.id);
    fetchFolderFiles(folder.id);
  };
  
  // Handle going back to project root
  const handleBackToProject = () => {
    setCurrentFolder(null);
    setSelectedFolderId(null);
    setFolderFiles([]);
  };

  // Handle download order confirmation PDF
  const handleDownloadOrderConfirmation = async () => {
    try {
      toast.info('Generating order specification PDF...');
      
      // Use jsPDF with enhanced styling
      const { jsPDF } = await import('jspdf');
      await generatePDFWithJsPDF(jsPDF);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF. Please try again.');
    }
  };
  
  // Generate print-optimized HTML
  const generateOrderConfirmationHTMLForPrint = () => {
    const doorRows = folders.map((folder, index) => {
      const doorNum = index + 1;
      const meta = folder.doorMetadata || {};
      const partNum = meta.itemNr || meta.item_nr || 'N/A';
      const doorType = getDoorTypeLabel(meta.doorName || folder.name);
      const dimensions = meta.width && meta.height ? `${meta.width}×${meta.height}mm` : 'N/A';
      const handedness = getHandednessLabel(meta.handedness);
      const lock = meta.lockType || 'No lock';
      const threshold = getThresholdTypeLabel(meta.thresholdType);
      const hinges = meta.hingeCount ? `${meta.hingeCount} hinges` : '2 hinges';
      const paint = meta.surfaceFinish && meta.colorCode ? `${getSurfaceFinishLabel(meta.surfaceFinish)} ${meta.colorCode}` : 'Untreated';
      const glass = meta.glassStyle && parseInt(meta.glassStyle) > 0 ? `Glass style ${meta.glassStyle}` : 'No glass';
      
      const features = [];
      if (meta.waternose) features.push('Waternose');
      if (meta.kickplate) features.push('Kickplate');
      if (meta.framePaint) features.push(`Frame: ${meta.framePaint}`);
      
      return `
        <tr style="${index % 2 === 0 ? 'background-color: #f9f9f9;' : ''}">
          <td style="padding: 10px 8px; font-weight: bold;">${doorNum}</td>
          <td style="padding: 10px 8px; font-weight: bold;">${partNum}</td>
          <td style="padding: 10px 8px;">
            <div style="font-weight: bold; margin-bottom: 4px;">${doorType}, ${dimensions}, ${handedness}</div>
            <div style="font-size: 10px; color: #555;">${lock}, ${hinges}</div>
            <div style="font-size: 10px; color: #555;">Threshold: ${threshold}, ${paint}</div>
            <div style="font-size: 10px; color: #555;">${glass}</div>
            ${features.length > 0 ? `<div style="font-size: 9px; color: #888; margin-top: 2px;">${features.join(', ')}</div>` : ''}
          </td>
          <td style="padding: 10px 8px; text-align: right; font-weight: bold;">${meta.amount || 1} pcs</td>
        </tr>
      `;
    }).join('');
    
    const totalDoors = folders.reduce((sum, f) => sum + (f.doorMetadata?.amount || 1), 0);
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Order Specification - ${project.project_number || project.name}</title>
        <style>
          @page {
            size: A4;
            margin: 15mm;
          }
          
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: 'Helvetica', 'Arial', sans-serif;
            color: #333;
            line-height: 1.6;
            font-size: 11px;
          }
          
          .header {
            background-color: #2c3e50;
            color: white;
            padding: 20px;
            margin-bottom: 30px;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          
          .logo-section {
            display: flex;
            align-items: center;
            gap: 15px;
          }
          
          .logo {
            max-height: 50px;
            max-width: 150px;
          }
          
          .company-info h1 {
            font-size: 24px;
            margin-bottom: 5px;
          }
          
          .company-info p {
            font-size: 10px;
            margin: 2px 0;
          }
          
          .order-info {
            text-align: right;
          }
          
          .order-info h2 {
            font-size: 18px;
            margin-bottom: 5px;
          }
          
          .order-info p {
            font-size: 12px;
            margin: 2px 0;
          }
          
          .details-section {
            display: flex;
            justify-content: space-between;
            margin-bottom: 30px;
          }
          
          .section {
            flex: 1;
            padding: 15px;
            background-color: #f8f9fa;
            border-radius: 5px;
            margin: 0 5px;
          }
          
          .section h3 {
            font-size: 12px;
            color: #2c3e50;
            margin-bottom: 10px;
            border-bottom: 2px solid #2c3e50;
            padding-bottom: 5px;
          }
          
          .section p {
            margin: 5px 0;
            font-size: 11px;
          }
          
          .section strong {
            font-weight: bold;
          }
          
          .separator {
            border-top: 2px solid #2c3e50;
            margin: 25px 0;
          }
          
          .table-title {
            font-size: 14px;
            font-weight: bold;
            color: #2c3e50;
            margin-bottom: 15px;
          }
          
          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 25px;
          }
          
          th {
            background-color: #2c3e50;
            color: white;
            padding: 12px 8px;
            text-align: left;
            font-weight: bold;
            font-size: 11px;
          }
          
          td {
            padding: 10px 8px;
            border-bottom: 1px solid #ecf0f1;
            font-size: 10px;
          }
          
          .summary {
            text-align: right;
            margin-top: 20px;
            padding: 15px;
            background-color: #f8f9fa;
            border-radius: 5px;
          }
          
          .summary p {
            font-size: 12px;
            margin: 5px 0;
          }
          
          .summary strong {
            font-weight: bold;
          }
          
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #ecf0f1;
            text-align: center;
            font-size: 9px;
            color: #95a5a6;
          }
          
          @media print {
            body {
              print-color-adjust: exact;
              -webkit-print-color-adjust: exact;
            }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo-section">
            <img src="/drawinggenerator/logo.png" alt="Hestia Doors" class="logo" />
            <div class="company-info">
              <h1>HESTIA DOORS</h1>
              <p>Päikese tn 1, Järve linnaosa</p>
              <p>EE 30221 Kohtla-Järve linn</p>
              <p>torben@hestiadoors.com</p>
            </div>
          </div>
          <div class="order-info">
            <h2>ORDER CONFIRMATION</h2>
            <p><strong>OC-${project.project_number || 'N/A'}</strong></p>
            <p>Date: ${new Date().toLocaleDateString('en-GB')}</p>
            ${project.due_date ? `<p>Delivery: ${new Date(project.due_date).toLocaleDateString('en-GB')}</p>` : ''}
          </div>
        </div>
        
        <div class="details-section">
          ${project.client ? `
          <div class="section">
            <h3>CUSTOMER</h3>
            <p><strong>${project.client.name || 'N/A'}</strong></p>
            ${project.client.company_name ? `<p>${project.client.company_name}</p>` : ''}
            ${project.client.email ? `<p>${project.client.email}</p>` : ''}
            ${project.client.phone ? `<p>Tel: ${project.client.phone}</p>` : ''}
          </div>
          ` : ''}
          
          <div class="section">
            <h3>PROJECT</h3>
            <p><strong>Name:</strong> ${project.name || 'N/A'}</p>
            <p><strong>Order:</strong> ${project.project_number || 'N/A'}</p>
            <p><strong>Status:</strong> ${project.status || 'N/A'}</p>
            <p><strong>Created:</strong> ${project.created_at ? new Date(project.created_at).toLocaleDateString('en-GB') : 'N/A'}</p>
          </div>
        </div>
        
        <div class="separator"></div>
        
        <div class="table-title">DOOR SPECIFICATIONS</div>
        
        <table>
          <thead>
            <tr>
              <th style="width: 8%;">#</th>
              <th style="width: 20%;">Part #</th>
              <th style="width: 57%;">Part Description</th>
              <th style="width: 15%; text-align: right;">Qty</th>
            </tr>
          </thead>
          <tbody>
            ${doorRows}
          </tbody>
        </table>
        
        <div class="summary">
          <p><strong>Total doors: ${totalDoors} pcs</strong></p>
        </div>
        
        <div class="footer">
          <p>Generated on ${new Date().toLocaleString('en-GB')}</p>
          <p>Powered by Hestia Management System</p>
        </div>
      </body>
      </html>
    `;
  };
  
  // PDF generation with jsPDF
  const generatePDFWithJsPDF = async (jsPDF) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    let yPos = margin;
    
    // Modern clean header - no background color
    // Load and add Hestia logo
    const loadLogo = () => {
      return new Promise((resolve) => {
        const logoImg = new Image();
        logoImg.onload = () => resolve(logoImg);
        logoImg.onerror = () => resolve(null);
        logoImg.src = '/drawinggenerator/logo.png';
      });
    };
    
    const logoImg = await loadLogo();
    if (logoImg) {
      // Logo on the left - moved down 20px from previous position
      const logoWidth = 55;
      const logoHeight = 35;
      doc.addImage(logoImg, 'PNG', margin, yPos - 10, logoWidth, logoHeight);
    }
    
    // Order Specification title - top right
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('Order Specification', pageWidth - margin, yPos - 3, { align: 'right' });
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    // Remove CO- prefix if it exists in project_number (avoid OS-CO- duplication)
    const orderNum = (project.project_number || 'N/A').replace(/^CO-/i, '');
    doc.text(`OS-${orderNum}`, pageWidth - margin, yPos + 5, { align: 'right' });
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(`Date: ${new Date().toLocaleDateString('en-GB')}`, pageWidth - margin, yPos + 12, { align: 'right' });
    if (project.due_date) {
      doc.text(`Delivery date: ${new Date(project.due_date).toLocaleDateString('en-GB')}`, pageWidth - margin, yPos + 18, { align: 'right' });
    }
    
    yPos = 45;
    
    // Separator line after header
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.3);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 10;
    
    // Two column layout: Customer (full details), Vendor
    const col1 = margin;
    const col2 = pageWidth / 2 + 10;
    
    doc.setTextColor(0, 0, 0);
    
    // Customer Information (Column 1 - Full width)
    let col1Y = yPos;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(100, 100, 100);
    doc.text('Customer:', col1, col1Y);
    col1Y += 5;
    
    console.log('PDF Generation - Client data:', project.client);
    
    if (project.client) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text(project.client.name || 'N/A', col1, col1Y);
      col1Y += 5;
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(60, 60, 60);
      
      if (project.client.contact_person) {
        doc.text(project.client.contact_person, col1, col1Y);
        col1Y += 4;
      }
      
      // Billing/Invoicing address - use address, city, country fields
      col1Y += 1;
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(100, 100, 100);
      doc.text('Invoicing address:', col1, col1Y);
      col1Y += 4;
      
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(60, 60, 60);
      
      if (project.client.billing_address) {
        const billingLines = (project.client.billing_address || '').split('\n').filter(line => line.trim());
        billingLines.forEach(line => {
          if (line.trim()) {
            doc.text(line.trim(), col1, col1Y);
            col1Y += 3.5;
          }
        });
      } else {
        // Use address, city, country if billing_address not available
        if (project.client.address) {
          doc.text(project.client.address, col1, col1Y);
          col1Y += 3.5;
        }
        if (project.client.city) {
          const cityLine = project.client.country ? `${project.client.city}, ${project.client.country}` : project.client.city;
          doc.text(cityLine, col1, col1Y);
          col1Y += 3.5;
        } else if (project.client.country) {
          doc.text(project.client.country, col1, col1Y);
          col1Y += 3.5;
        }
      }
      
      // Shipping address
      if (project.client.shipping_address && project.client.shipping_address !== project.client.billing_address) {
        col1Y += 1;
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(100, 100, 100);
        doc.text('Shipping address:', col1, col1Y);
        col1Y += 4;
        
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(60, 60, 60);
        const shippingLines = (project.client.shipping_address || '').split('\n').filter(line => line.trim());
        shippingLines.forEach(line => {
          if (line.trim()) {
            doc.text(line.trim(), col1, col1Y);
            col1Y += 3.5;
          }
        });
      }
      
      col1Y += 1;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(60, 60, 60);
      
      if (project.client.reg_no) {
        doc.text(`Reg. no.: ${project.client.reg_no}`, col1, col1Y);
        col1Y += 3.5;
      }
      
      if (project.client.vat_number) {
        doc.text(`Tax/VAT number: ${project.client.vat_number}`, col1, col1Y);
        col1Y += 3.5;
      }
      
      // Order and Reference fields
      col1Y += 1;
      if (project.project_number) {
        const cleanOrderNum = (project.project_number || '').replace(/^CO-/i, '');
        doc.text(`Order: CO${cleanOrderNum}`, col1, col1Y);
        col1Y += 3.5;
      }
      
      if (project.reference) {
        doc.text(`Reference: ${project.reference}`, col1, col1Y);
        col1Y += 3.5;
      }
      
      // Delivery terms
      if (project.delivery_terms) {
        doc.text(`Delivery terms:`, col1, col1Y);
        col1Y += 3.5;
      }
      
      col1Y += 1;
      
      if (project.client.email) {
        doc.text(`E-mail: ${project.client.email}`, col1, col1Y);
        col1Y += 3.5;
      }
      
      if (project.client.phone) {
        doc.text(`Tel: ${project.client.phone}`, col1, col1Y);
        col1Y += 3.5;
      }
    } else {
      // Fallback if no client data
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(150, 150, 150);
      doc.text('No customer information available', col1, col1Y);
      col1Y += 5;
    }
    
    // Vendor Information (Column 2)
    let col2Y = yPos;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(100, 100, 100);
    doc.text('Vendor:', col2, col2Y);
    col2Y += 5;
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Hestia Doors OÜ', col2, col2Y);
    col2Y += 6;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(100, 100, 100);
    doc.text('Address:', col2, col2Y);
    col2Y += 4;
    
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text('Päikese tn 1,', col2, col2Y);
    col2Y += 4;
    doc.text('Järve linnaosa,', col2, col2Y);
    col2Y += 4;
    doc.text('EE 30221 Kohtla-Järve linn', col2, col2Y);
    col2Y += 6;
    
    doc.text('E-mail:', col2, col2Y);
    col2Y += 4;
    doc.text('torben@hestiadoors.com', col2, col2Y);
    col2Y += 6;
    
    doc.text('Payment details:', col2, col2Y);
    col2Y += 4;
    doc.text('SEB EE651010220294317227', col2, col2Y);
    
    yPos = Math.max(col1Y, col2Y) + 10;
    
    // Separator line
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.3);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 10;
    
    // Door List Table Header
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Door Specifications', margin, yPos);
    yPos += 8;
    
    // Table header with modern gray background
    doc.setFillColor(245, 245, 245); // Light gray
    doc.rect(margin, yPos - 5, pageWidth - 2 * margin, 7, 'F');
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(60, 60, 60);
    const colPos = margin + 2;
    const colPart = colPos + 12;
    const colDesc = colPart + 35; // Wider part column (was 25, now 35)
    const colQty = pageWidth - margin - 15;
    
    doc.text('#', colPos, yPos);
    doc.text('Part #', colPart, yPos);
    doc.text('Part Description', colDesc, yPos);
    doc.text('Qty', colQty, yPos);
    yPos += 7;
    
    doc.setTextColor(0, 0, 0); // Reset to black for content
    
    // Door rows with detailed specifications
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    
    folders.forEach((folder, index) => {
      if (yPos > pageHeight - 40) {
        doc.addPage();
        yPos = margin;
      }
      
      const doorNum = index + 1;
      const meta = folder.doorMetadata || {};
      
      // Part number (item_nr)
      const partNum = meta.itemNr || meta.item_nr || 'N/A';
      
      // Build detailed description
      const doorType = getDoorTypeLabel(meta.doorName || folder.name);
      const dimensions = meta.width && meta.height ? `${meta.width}×${meta.height}mm` : 'N/A';
      const handedness = getHandednessLabel(meta.handedness);
      const lock = meta.lockType || 'No lock';
      const threshold = getThresholdTypeLabel(meta.thresholdType);
      const hinges = meta.hingeCount ? `${meta.hingeCount} hinges` : '2 hinges';
      const paint = meta.surfaceFinish && meta.colorCode ? `${getSurfaceFinishLabel(meta.surfaceFinish)} ${meta.colorCode}` : 'Untreated';
      const glass = meta.glassStyle && parseInt(meta.glassStyle) > 0 ? `Glass style ${meta.glassStyle}` : 'No glass';
      
      // Row background (alternating)
      if (index % 2 === 0) {
        doc.setFillColor(249, 249, 249);
        doc.rect(margin, yPos - 4, pageWidth - 2 * margin, 22, 'F');
      }
      
      doc.setFont('helvetica', 'bold');
      doc.text(doorNum.toString(), colPos, yPos);
      
      // Part number - use smaller font and split if too long to prevent line breaks
      doc.setFontSize(7);
      const maxPartWidth = 23; // Max width for part number column
      if (doc.getTextWidth(partNum) > maxPartWidth) {
        // Split long part numbers into multiple lines
        const partLines = doc.splitTextToSize(partNum, maxPartWidth);
        let partY = yPos;
        partLines.forEach((line, idx) => {
          doc.text(line, colPart, partY);
          if (idx < partLines.length - 1) partY += 3;
        });
      } else {
        doc.text(partNum, colPart, yPos);
      }
      doc.setFontSize(8);
      
      doc.setFont('helvetica', 'normal');
      // Description line 1
      doc.text(`${doorType}, ${dimensions}, ${handedness}`, colDesc, yPos);
      yPos += 4;
      
      // Description line 2
      doc.text(`${lock}, ${hinges}`, colDesc, yPos);
      yPos += 4;
      
      // Description line 3
      doc.text(`Threshold: ${threshold}, ${paint}`, colDesc, yPos);
      yPos += 4;
      
      // Description line 4
      doc.text(`${glass}`, colDesc, yPos);
      yPos += 4;
      
      // Additional features
      const features = [];
      if (meta.waternose) features.push('Waternose');
      if (meta.kickplate) features.push('Kickplate');
      if (meta.framePaint) features.push(`Frame: ${meta.framePaint}`);
      if (features.length > 0) {
        doc.setFontSize(7);
        doc.setTextColor(100, 100, 100);
        doc.text(features.join(', '), colDesc, yPos);
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(8);
        yPos += 4;
      }
      
      // Quantity
      doc.setFont('helvetica', 'bold');
      doc.text(`${meta.amount || 1} pcs`, colQty, yPos - 12);
      
      yPos += 6;
    });
    
    // Summary section
    yPos += 5;
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.3);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 8;
    
    const totalDoors = folders.reduce((sum, f) => sum + (f.doorMetadata?.amount || 1), 0);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text(`Total doors: ${totalDoors} pcs`, pageWidth - margin, yPos, { align: 'right' });
    
    // Footer
    yPos = pageHeight - 15;
    doc.setFontSize(7);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(150, 150, 150);
    doc.text(`Generated on ${new Date().toLocaleString('en-GB')}`, pageWidth / 2, yPos, { align: 'center' });
    doc.text('Powered by Hestia Management System', pageWidth / 2, yPos + 4, { align: 'center' });
    
    // Save PDF
    const fileName = `Order_Specification_${project.project_number || project.name}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    
    toast.success('Order specification downloaded successfully');
  };
  
  // Handle finding similar doors across all projects
  const handleFindSimilarDoors = async (itemNr) => {
    try {
      setLoadingSimilarDoors(true);
      setShowSimilarDoorsModal(true);
      
      console.log('🔍 Searching for doors with item number:', itemNr);
      
      // Query all door metadata with the same item number
      const { data: doorsData, error: doorsError } = await supabase
        .from('door_metadata')
        .select(`
          *,
          folders!inner(
            id,
            name,
            project_id,
            projects!inner(
              id,
              name,
              status
            )
          )
        `)
        .eq('item_nr', itemNr);
      
      if (doorsError) throw doorsError;
      
      console.log('✅ Found similar doors:', doorsData);
      
      // Format the data for display
      const formattedDoors = doorsData.map(door => ({
        doorId: door.id,
        folderId: door.folders.id,
        folderName: door.folders.name,
        projectId: door.folders.projects.id,
        projectName: door.folders.projects.name,
        projectStatus: door.folders.projects.status,
        itemNr: door.item_nr,
        barcodeUrl: door.barcode_url,
        width: door.width,
        height: door.height,
        doorName: door.door_name,
        amount: door.amount,
        createdAt: door.created_at
      }));
      
      setSimilarDoors(formattedDoors);
      toast.success(`Found ${formattedDoors.length} door(s) with the same specifications`);
    } catch (error) {
      console.error('Error finding similar doors:', error);
      toast.error('Failed to find similar doors');
      setSimilarDoors([]);
    } finally {
      setLoadingSimilarDoors(false);
    }
  };

  // Handle opening a file in the built-in viewer modal
  const handleViewFile = async (file) => {
    try {
      console.log('📖 Opening file in viewer:', file.original_name);
      
      // Set the selected file for the modal viewer
      setSelectedFileForViewing(file);
      
      // Show the file viewer modal
      setShowFileViewerModal(true);
      
      toast.success('File opened in viewer!');
    } catch (error) {
      console.error('Error opening file:', error);
      toast.error('Failed to open file. Please try again.');
    }
  };

  // Handle downloading a file with proper authenticated request
  const handleDownloadFile = async (file) => {
    try {
      setLoading(true);
      toast.info('Preparing file for download...');
      
      // Get signed URL from Supabase Storage
      const downloadUrl = await fileService.getFileUrl(file.file_path);
      
      // Fetch the file
      const response = await fetch(downloadUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      // Create a download link
      const link = document.createElement('a');
      link.href = url;
      link.download = file.originalName || 'download'; // This forces download rather than navigation
      document.body.appendChild(link);
      
      // Trigger download and clean up
      link.click();
      
      // Clean up
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      }, 100);
      
      toast.success('Download started!');
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle folder deletion
  const handleDeleteFolder = async (folderId) => {
    if (window.confirm('Are you sure you want to delete this folder? This will also delete all associated data.')) {
      try {
        await folderService.deleteFolder(folderId);
        toast.success('Folder deleted successfully');
        
        // Update folders list
        setFolders(prevFolders => prevFolders.filter(folder => folder.id !== folderId));
      } catch (err) {
        toast.error(err.message || 'Failed to delete folder');
        console.error('Error deleting folder:', err);
      }
    }
  };

  // Handle copy door
  const handleCopyDoor = async (folder) => {
    console.log('🔄 Copying door:', folder);
    try {
      setDoorToCopy(folder);
      setCopyDoorName(folder.name); // Use original name as-is
      setCopyToProjectId(id); // Default to current project
      
      // Fetch available projects
      const projects = await projectService.getProjects();
      setAvailableProjects(projects);
      
      setShowCopyModal(true);
    } catch (error) {
      console.error('Error in handleCopyDoor:', error);
      toast.error('Failed to open copy dialog');
    }
  };

  // Execute door copy
  const executeCopyDoor = async () => {
    if (!doorToCopy) {
      toast.error('No door selected for copying');
      return;
    }

    if (!copyToProjectId) {
      toast.error('Please select a project to copy to');
      return;
    }

    try {
      setLoading(true);
      
      // Create new folder in the selected project
      const newFolder = await folderService.createFolder(
        copyToProjectId, // Use selected project ID
        doorToCopy.name, // Use original door name
        'door' // folderType
      );

      // Copy door metadata if it exists
      if (doorToCopy.doorMetadata) {
        await doorService.saveDoorMetadata(newFolder.id, {
          width: doorToCopy.doorMetadata.width,
          height: doorToCopy.doorMetadata.height,
          doorName: doorToCopy.doorMetadata.doorName,
          hasGlass: doorToCopy.doorMetadata.hasGlass,
          hingeType: doorToCopy.doorMetadata.hingeType,
          lockType: doorToCopy.doorMetadata.lockType,
          amount: doorToCopy.doorMetadata.amount,
          waternose: doorToCopy.doorMetadata.waternose,
          thresholdType: doorToCopy.doorMetadata.thresholdType,
          kickplate: doorToCopy.doorMetadata.kickplate,
          surfaceFinish: doorToCopy.doorMetadata.surfaceFinish,
          customFinish: doorToCopy.doorMetadata.customFinish,
          colorCode: doorToCopy.doorMetadata.colorCode,
          handedness: doorToCopy.doorMetadata.handedness
        });
      }

      const targetProject = availableProjects.find(p => p.id === copyToProjectId);
      const isCurrentProject = copyToProjectId === id;
      
      if (isCurrentProject) {
        toast.success(`Door "${doorToCopy.name}" copied successfully!`);
        fetchProjectDetails(); // Refresh current project
      } else {
        toast.success(`Door "${doorToCopy.name}" copied to project "${targetProject?.project_number || 'Unknown'}" successfully!`);
        // No need to refresh since it's copied to another project
      }
      
      setShowCopyModal(false);
      setDoorToCopy(null);
      setCopyToProjectId('');
      setAvailableProjects([]);
    } catch (error) {
      toast.error('Failed to copy door');
      console.error('Error copying door:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle showing door details
  const handleShowDoorDetails = (folder) => {
    console.log('🔍 Showing door details:', folder);
    setSelectedDoorForDetails(folder);
    setShowDetailsModal(true);
  };

  // Handle opening drawing generator
  const handleOpenDrawingGenerator = (folder) => {
    console.log('🎨 Opening drawing generator for door:', folder);
    if (!folder.doorMetadata) {
      toast.error('Please add door specifications first before generating drawings');
      return;
    }
    
    // Calculate door number based on position in folders array
    const doorNumber = folders.findIndex(f => f.id === folder.id) + 1;
    
    // Add door number to the folder object
    const folderWithNumber = {
      ...folder,
      doorNumber: doorNumber
    };
    
    setSelectedDoorForDrawing(folderWithNumber);
    setShowDrawingGeneratorModal(true);
  };

  // Handle PDF saved from drawing generator
  const handlePdfSaved = (saveResult) => {
    console.log('📄 PDF saved:', saveResult);
    // Refresh the folder files to show the new PDF
    if (currentFolder && currentFolder.id === selectedDoorForDrawing?.id) {
      fetchFolderFiles(currentFolder.id);
    }
    // Refresh folders list to update drawing status
    fetchProjectDetails();
  };

  // Helper function to format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Handle file deletion
  const handleDeleteFile = async (fileId) => {
    if (window.confirm('Are you sure you want to delete this file? This action cannot be undone.')) {
      try {
        console.log('Deleting file with ID:', fileId);
        await fileService.deleteFile(fileId);
        toast.success('File deleted successfully');
        
        // Refresh to remove from UI
        if (currentFolder) {
          fetchFolderFiles(currentFolder.id);
        } else {
          fetchProjectDetails();
        }
      } catch (error) {
        console.error('Error deleting file:', error);
        
        // If file doesn't exist in storage but exists in database, try to clean up database
        if (error.message && error.message.includes('not found')) {
          try {
            // Try to delete from database only
            const { error: dbError } = await supabase
              .from('files')
              .delete()
              .eq('id', fileId);
            
            if (!dbError) {
              toast.success('Phantom file removed from database');
              if (currentFolder) {
                fetchFolderFiles(currentFolder.id);
              } else {
                fetchProjectDetails();
              }
              return;
            }
          } catch (cleanupError) {
            console.error('Cleanup error:', cleanupError);
          }
        }
        
        toast.error('Failed to delete file: ' + (error.message || 'Unknown error'));
      }
    }
  };

  // Add error boundary logging
  console.log('🔍 ProjectDetails render state:', {
    loading,
    error,
    project: !!project,
    folders: folders?.length || 0,
    showCopyModal,
    doorToCopy: !!doorToCopy
  });

  return (
    <Container>
      <div className="mb-4">
        <Link to="/admin/dashboard" className="btn btn-link ps-0">
          <FaArrowLeft /> Back to Projects
        </Link>
      </div>
      
      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : project ? (
        <>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2>{project.project_number}</h2>
              <h5>{project.name}</h5>
              {project.client && (
                <div className="mb-2">
                  <small className="text-muted">
                    <strong>Client:</strong> {project.client.name}
                    {project.client.company_name && ` (${project.client.company_name})`}
                  </small>
                </div>
              )}
              <div className="d-flex align-items-center gap-3 mt-2">
                <span className={`badge ${getBadgeColor(project.status)} fs-6`}>
                  {project.status || 'New'}
                </span>
                {project.created_at && (
                  <span className="text-muted">
                    <strong>Created:</strong> {new Date(project.created_at).toLocaleDateString()}
                  </span>
                )}
                {project.due_date && (
                  <span className="text-muted">
                    <strong>Due:</strong> {new Date(project.due_date).toLocaleDateString()}
                  </span>
                )}
                <Button 
                  variant="outline-secondary" 
                  size="sm"
                  onClick={() => {
                    setEditProjectData({
                      name: project.name,
                      description: project.description || '',
                      status: project.status,
                      dueDate: project.due_date || '',
                      clientId: project.client_id || null
                    });
                    fetchClients();
                    setShowProjectEditModal(true);
                  }}
                >
                  <FaEdit className="me-1" /> Edit Project
                </Button>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={handleDownloadOrderConfirmation}
                >
                  <FaDownload className="me-1" /> Order Specification
                </Button>
              </div>
            </div>
            <div>
              <Button 
                variant="primary"
                onClick={() => setShowFolderModal(true)}
              >
                <FaFolderPlus className="me-1" /> Create Door
              </Button>
            </div>
          </div>

          <Tabs defaultActiveKey="doors" className="mb-4">
            <Tab eventKey="doors" title="Doors">
              <Card>
                {currentFolder ? (
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <Button 
                        variant="link" 
                        className="p-0" 
                        onClick={handleBackToProject}
                      >
                        <FaArrowLeft className="me-1" /> Back to Doors
                      </Button>
                      <Button 
                        variant="outline-info" 
                        size="sm" 
                        onClick={() => handleShowDoorDetails(currentFolder)}
                      >
                        <FaInfoCircle className="me-1" /> Raw Data
                      </Button>
                    </div>

                    <div className="d-flex align-items-center mb-3">
                      <RenderWindow 
                        doorMetadata={currentFolder.doorMetadata}
                        size="small"
                        className="me-3"
                      />
                      <div>
                        <h5 className="mb-0">{currentFolder.name}</h5>
                        {project?.project_number && (
                          <small className="text-muted">
                            {project.project_number}-{folders.findIndex(f => f.id === currentFolder.id) + 1}
                          </small>
                        )}
                      </div>
                    </div>

                    {currentFolder.doorMetadata ? (
                      <Card className="mb-4 bg-light">
                        <Card.Body>
                          <h6>Door Specifications</h6>
                          <Row>
                            <Col md={4}>
                              <p className="mb-1"><strong>Type:</strong> {getDoorTypeLabel(currentFolder.doorMetadata.doorName)}</p>
                              <p className="mb-1"><strong>Dimensions:</strong> {currentFolder.doorMetadata.width} × {currentFolder.doorMetadata.height} mm</p>
                              <p className="mb-1"><strong>Quantity:</strong> {currentFolder.doorMetadata.amount}</p>
                              <p className="mb-1"><strong>Handedness:</strong> {getHandednessLabel(currentFolder.doorMetadata.handedness)}</p>
                              <p className="mb-1"><strong>Door Status:</strong> {getDoorStatusLabel(currentFolder.doorMetadata.doorStatus)}</p>
                            </Col>
                            <Col md={4}>
                              <p className="mb-1"><strong>Glass Style:</strong> {currentFolder.doorMetadata.glassStyle || '00'}</p>
                              {currentFolder.doorMetadata.hasGlass && (
                                <>
                                  <p className="mb-1"><strong>Glass Size:</strong> {currentFolder.doorMetadata.glassWidth} × {currentFolder.doorMetadata.glassHeight} mm</p>
                                </>
                              )}
                              <p className="mb-1"><strong>Groove Design:</strong> {currentFolder.doorMetadata.grooveDesign || '000'}</p>
                              <p className="mb-1"><strong>Hinge:</strong> {getHingeTypeLabel(currentFolder.doorMetadata.hingeType)} ({currentFolder.doorMetadata.hingeCount || '2'} pcs)</p>
                              <p className="mb-1"><strong>Lock:</strong> {getLockTypeLabel(currentFolder.doorMetadata.lockType)}</p>
                            </Col>
                            <Col md={4}>
                              <p className="mb-1"><strong>Waternose:</strong> {currentFolder.doorMetadata.waternose ? 'W1 - Yes' : 'W0 - None'}</p>
                              <p className="mb-1"><strong>Threshold:</strong> {getThresholdTypeLabel(currentFolder.doorMetadata.thresholdType)}</p>
                              <p className="mb-1"><strong>Kickplate:</strong> {currentFolder.doorMetadata.kickplate ? 'K1 - Yes' : 'K0 - None'}</p>
                              <p className="mb-1"><strong>Special Feature:</strong> {currentFolder.doorMetadata.specialFeature ? 'X - Special' : 'S - Standard'}</p>
                              {currentFolder.doorMetadata.surfaceFinish && (
                                <p className="mb-1">
                                  <strong>Paint Type:</strong> {getSurfaceFinishLabel(currentFolder.doorMetadata.surfaceFinish)}
                                </p>
                              )}
                              {currentFolder.doorMetadata.colorCode && (
                                <p className="mb-1">
                                  <strong>Paint Code:</strong> {currentFolder.doorMetadata.colorCode}
                                </p>
                              )}
                            </Col>
                            <Col md={4} className="text-center">
                              <RenderWindow 
                                doorMetadata={currentFolder.doorMetadata}
                                size="large"
                                showDetails={false}
                              />
                            </Col>
                          </Row>
                          
                          {/* Item Number and Barcode Section */}
                          {currentFolder.doorMetadata.itemNr && (
                            <div className="mt-4 pt-3 border-top">
                              <Row>
                                <Col md={8}>
                                  <h6 className="mb-2">Item Number (Article Number)</h6>
                                  <div className="d-flex align-items-center mb-2">
                                    <code className="bg-white px-3 py-2 rounded border flex-grow-1 me-2" style={{ fontSize: '0.95rem' }}>
                                      {currentFolder.doorMetadata.itemNr}
                                    </code>
                                    <Button 
                                      variant="outline-secondary" 
                                      size="sm"
                                      onClick={() => {
                                        navigator.clipboard.writeText(currentFolder.doorMetadata.itemNr);
                                        toast.success('Item number copied to clipboard!');
                                      }}
                                    >
                                      <FaCopy /> Copy
                                    </Button>
                                  </div>
                                </Col>
                                <Col md={4} className="text-center">
                                  {currentFolder.doorMetadata.barcodeUrl && (
                                    <div>
                                      <h6 className="mb-2">Barcode</h6>
                                      <img 
                                        src={currentFolder.doorMetadata.barcodeUrl} 
                                        alt="Door Barcode"
                                        style={{ maxWidth: '100%', height: 'auto', border: '1px solid #dee2e6', borderRadius: '4px', padding: '8px', background: 'white', marginBottom: '8px' }}
                                      />
                                      <div className="d-flex gap-2 justify-content-center">
                                        <Button
                                          variant="outline-primary"
                                          size="sm"
                                          onClick={() => window.open(currentFolder.doorMetadata.barcodeUrl, '_blank')}
                                        >
                                          <FaEye /> View
                                        </Button>
                                        <Button
                                          variant="outline-success"
                                          size="sm"
                                          onClick={async () => {
                                            try {
                                              const response = await fetch(currentFolder.doorMetadata.barcodeUrl);
                                              const blob = await response.blob();
                                              const url = window.URL.createObjectURL(blob);
                                              const a = document.createElement('a');
                                              a.href = url;
                                              a.download = `barcode_${currentFolder.doorMetadata.itemNr}.png`;
                                              document.body.appendChild(a);
                                              a.click();
                                              window.URL.revokeObjectURL(url);
                                              document.body.removeChild(a);
                                              toast.success('Barcode downloaded!');
                                            } catch (error) {
                                              console.error('Error downloading barcode:', error);
                                              toast.error('Failed to download barcode');
                                            }
                                          }}
                                        >
                                          <FaDownload /> Download
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                </Col>
                              </Row>
                            </div>
                          )}
                          
                          <div className="mt-3 d-flex gap-2 flex-wrap">
                            <Button 
                              variant="outline-secondary" 
                              size="sm" 
                              onClick={() => handleFolderSelect(currentFolder)}
                            >
                              <FaEdit /> Edit Door Data
                            </Button>
                            {currentFolder.doorMetadata.itemNr && (
                              <Button 
                                variant="outline-info" 
                                size="sm" 
                                onClick={() => handleFindSimilarDoors(currentFolder.doorMetadata.itemNr)}
                              >
                                <FaInfoCircle /> Find Similar Doors
                              </Button>
                            )}
                            {currentFolder.doorMetadata && (
                              <>
                                <Button 
                                  variant="success" 
                                  size="sm" 
                                  onClick={() => {
                                    setSelectedDoorForDrawing(currentFolder);
                                    setShowDrawingGeneratorModal(true);
                                  }}
                                >
                                  <FaDraftingCompass /> Generate Drawings
                                </Button>
                                <Button 
                                  variant="primary" 
                                  size="sm" 
                                  onClick={() => {
                                    setCurrentFolder(currentFolder);
                                    setSelectedFolderId(currentFolder.id);
                                    setShowUploadModal(true);
                                  }}
                                >
                                  <FaUpload /> Add Drawings
                                </Button>
                              </>
                            )}
                          </div>
                        </Card.Body>
                      </Card>
                    ) : (
                      <Card className="mb-4 bg-light">
                        <Card.Body className="text-center">
                          <p>No door data available for this door.</p>
                          <Button 
                            variant="primary" 
                            size="sm" 
                            onClick={() => handleFolderSelect(currentFolder)}
                          >
                            Add Door Data
                          </Button>
                        </Card.Body>
                      </Card>
                    )}

                    <h6 className="mt-4">Door Drawings</h6>
                    {folderFiles.length === 0 ? (
                      <div
                        ref={dropAreaRef}
                        className={`border rounded p-4 text-center ${isDragOver ? 'border-primary bg-light' : 'border-secondary'}`}
                        style={{
                          borderStyle: 'dashed',
                          borderWidth: '2px',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease'
                        }}
                        onDragEnter={handleDragEnter}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <FaUpload size={48} className={`mb-3 ${isDragOver ? 'text-primary' : 'text-muted'}`} />
                        <h5>{isDragOver ? '📂 Drop files here!' : '📤 Drop files here or click to browse'}</h5>
                        <p className="text-muted mb-0">
                          {isDragOver ? 'Release to upload' : 'Supported: PDF, Images, Excel files (Max 50MB)'}
                        </p>
                      </div>
                    ) : (
                      <ListGroup variant="flush">
                        {folderFiles.map(file => (
                          <ListGroup.Item key={file.id} className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center">
                              <FaFilePdf className="text-danger me-3" size={24} />
                              <div>
                                <div className="fw-bold">{file.original_name || file.originalName}</div>
                                <small className="text-muted">
                                  {formatFileSize(file.file_size || file.fileSize)}
                                </small>
                              </div>
                            </div>
                            <div>
                              <Button 
                                variant="outline-primary" 
                                size="sm" 
                                className="me-2"
                                onClick={() => handleViewFile(file)}
                              >
                                <FaEye /> View
                              </Button>
                              <Button 
                                variant="outline-primary" 
                                size="sm" 
                                className="me-2"
                                onClick={() => handleDownloadFile(file)}
                              >
                                <FaDownload /> Download
                              </Button>
                              <Button 
                                variant="outline-danger" 
                                size="sm"
                                onClick={() => handleDeleteFile(file.id)}
                              >
                                <FaTrash />
                              </Button>
                            </div>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    )}
                  </Card.Body>
                ) : (
                  <Card.Body>
                    {folders.length === 0 ? (
                      <Alert variant="info">
                        No doors created yet. Click the "Create Door" button to add a door to this project.
                      </Alert>
                    ) : (
                      <>
                        {/* Drawing Status Summary */}
                        {(() => {
                          const doorsWithDrawings = folders.filter(hasPdfDrawings).length;
                          const totalDoors = folders.length;
                          const percentage = Math.round((doorsWithDrawings / totalDoors) * 100);
                          
                          if (doorsWithDrawings === totalDoors) {
                            return (
                              <Alert variant="success" className="mb-3">
                                <FaCheck className="me-2" />
                                <strong>All doors have drawings!</strong> ({totalDoors}/{totalDoors} complete)
                              </Alert>
                            );
                          } else if (doorsWithDrawings === 0) {
                            return (
                              <Alert variant="warning" className="mb-3">
                                <FaExclamationTriangle className="me-2" />
                                <strong>No doors have drawings yet.</strong> Upload PDF drawings to complete your project.
                              </Alert>
                            );
                          } else {
                            return (
                              <Alert variant="info" className="mb-3">
                                <FaExclamationTriangle className="me-2" />
                                <strong>Drawing progress: {percentage}%</strong> ({doorsWithDrawings}/{totalDoors} doors have drawings)
                              </Alert>
                            );
                          }
                        })()}
                        
                        <ListGroup variant="flush">
                        {folders.map((folder, index) => (
                          <ListGroup.Item key={folder.id} className="d-flex justify-content-between align-items-center">
                            <div 
                              className="d-flex align-items-center cursor-pointer" 
                              style={{ cursor: 'pointer' }}
                              onClick={() => handleOpenFolder(folder)}
                            >
                              {/* Door render window with conditional styling based on PDF presence */}
                              <div className="position-relative me-3">
                                <RenderWindow 
                                  doorMetadata={folder.doorMetadata}
                                  size="small"
                                  style={{
                                    border: hasPdfDrawings(folder) ? '3px solid #28a745' : '3px solid #ffc107'
                                  }}
                                />
                                {hasPdfDrawings(folder) && (
                                  <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-success" style={{ fontSize: '0.5rem' }}>
                                    <FaCheck size={8} />
                                  </span>
                                )}
                              </div>
                              <div>
                                <div className="fw-bold">
                                  <span className="badge bg-primary me-2">Pos {index + 1}</span>
                                  {folder.doorMetadata ? getDoorTypeLabel(folder.doorMetadata.doorName) : getDoorTypeLabel(folder.name)}
                                </div>
                                <small>
                                  {folder.doorMetadata ? (
                                    <>
                                      <div className="text-muted mb-1">
                                        {`${folder.doorMetadata.width}×${folder.doorMetadata.height}mm`}
                                        {folder.doorMetadata.amount && (
                                          <span className="ms-2 badge bg-secondary">{folder.doorMetadata.amount} pcs</span>
                                        )}
                                      </div>
                                      <div className="d-flex flex-wrap gap-2">
                                        {folder.doorMetadata.handedness && (
                                          <span className="badge bg-info">{getHandednessLabel(folder.doorMetadata.handedness)}</span>
                                        )}
                                        {folder.doorMetadata.surfaceFinish && (
                                          <span className="badge bg-warning text-dark">
                                            {getSurfaceFinishLabel(folder.doorMetadata.surfaceFinish)}
                                            {folder.doorMetadata.colorCode && (folder.doorMetadata.surfaceFinish === 'R' || folder.doorMetadata.surfaceFinish === 'N') && (
                                              <>
                                                {' '}{folder.doorMetadata.colorCode}
                                                <span
                                                  className="ms-1 d-inline-block border rounded"
                                                  style={{
                                                    width: '12px',
                                                    height: '12px',
                                                    backgroundColor: getColorPreview(folder.doorMetadata.surfaceFinish === 'R' ? 'RAL' : 'NCS', folder.doorMetadata.colorCode),
                                                    verticalAlign: 'middle'
                                                  }}
                                                />
                                              </>
                                            )}
                                          </span>
                                        )}
                                        {hasPdfDrawings(folder) ? (
                                          <span className="badge bg-success">
                                            <FaCheck className="me-1" size={10} />
                                            Drawings available
                                          </span>
                                        ) : (
                                          <span className="badge bg-warning text-dark">
                                            <FaExclamationTriangle className="me-1" size={10} />
                                            Missing drawings
                                          </span>
                                        )}
                                      </div>
                                    </>
                                  ) : (
                                    <span className="text-muted">No specifications yet</span>
                                  )}
                                </small>
                              </div>
                            </div>
                            <div>
                              <Button 
                                variant="outline-secondary" 
                                size="sm" 
                                className="me-2"
                                onClick={() => handleFolderSelect(folder)}
                              >
                                Edit Data
                              </Button>
                              <Button 
                                variant="outline-success" 
                                size="sm" 
                                className="me-2"
                                onClick={() => handleOpenDrawingGenerator(folder)}
                                title="Auto-generate drawings"
                                disabled={!folder.doorMetadata}
                              >
                                <FaDraftingCompass />
                              </Button>
                              <Button 
                                variant="outline-info" 
                                size="sm" 
                                className="me-2"
                                onClick={() => handleShowDoorDetails(folder)}
                                title="View Raw Data"
                              >
                                <FaInfoCircle />
                              </Button>
                              <Button 
                                variant="outline-primary" 
                                size="sm" 
                                className="me-2"
                                onClick={() => handleCopyDoor(folder)}
                                title="Copy Door"
                              >
                                <FaCopy />
                              </Button>
                              <Button 
                                variant="outline-danger" 
                                size="sm"
                                onClick={() => handleDeleteFolder(folder.id)}
                              >
                                <FaTrash />
                              </Button>
                            </div>
                          </ListGroup.Item>
                        ))}
                        </ListGroup>
                      </>
                    )}
                  </Card.Body>
                )}
              </Card>
            </Tab>
            
            <Tab eventKey="files" title="Files">
              <Card>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5>Project Files</h5>
                    <Button 
                      variant="outline-primary" 
                      size="sm" 
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <FaUpload className="me-1" /> Browse Files
                    </Button>
                  </div>
                  
                  {/* Drag & Drop Zone */}
                  <div 
                    className={`border-2 border-dashed rounded p-4 mb-3 text-center transition-all position-relative ${
                      isDragOver ? 'border-primary bg-primary bg-opacity-10' : 'border-secondary'
                    } ${uploading ? 'opacity-75' : ''}`}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    style={{ 
                      minHeight: '120px', 
                      cursor: uploading ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease-in-out'
                    }}
                    onClick={() => !uploading && fileInputRef.current?.click()}
                  >
                    {uploading && (
                      <div className="position-absolute top-50 start-50 translate-middle">
                        <Spinner animation="border" variant="primary" />
                      </div>
                    )}
                    <FaUpload 
                      className={`mb-2 ${isDragOver ? 'text-primary' : 'text-muted'} ${uploading ? 'opacity-50' : ''}`} 
                      size={isDragOver ? 40 : 32} 
                    />
                    <p className={`mb-0 fw-bold ${isDragOver ? 'text-primary' : 'text-muted'} ${uploading ? 'opacity-50' : ''}`}>
                      {uploading ? 'Uploading files...' : isDragOver ? '📁 Drop files here!' : 'Drag & drop files here or click to browse'}
                    </p>
                    <small className={`text-muted ${uploading ? 'opacity-50' : ''}`}>
                      {uploading ? 'Please wait...' : 'Supported: PDF, Images (JPG, PNG, GIF), Excel (XLSX, XLS), Email (MSG, EML)'}
                    </small>
                  </div>
                  
                  {files.length === 0 ? (
                    <Alert variant="info">
                      No files uploaded yet. Drag & drop files above or click "Browse Files" to upload.
                    </Alert>
                  ) : (
                    <ListGroup variant="flush">
                      {files.filter(file => !file.folder_id).map(file => (
                        <ListGroup.Item key={file.id} className="d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center">
                            {file.original_name.toLowerCase().endsWith('.pdf') ? (
                              <FaFilePdf className="text-danger me-3" size={24} />
                            ) : file.original_name.toLowerCase().match(/\.(jpe?g|png|gif)$/i) ? (
                              <FaFileImage className="text-primary me-3" size={24} />
                            ) : file.original_name.toLowerCase().match(/\.(xlsx|xls)$/i) ? (
                              <FaFileExcel className="text-success me-3" size={24} />
                            ) : (
                              <FaFile className="text-secondary me-3" size={24} />
                            )}
                            <div>
                              <div className="fw-bold">{file.original_name}</div>
                              <small className="text-muted">
                                {formatFileSize(file.file_size)}
                              </small>
                            </div>
                          </div>
                          <div>
                            <Button 
                              variant="outline-primary" 
                              size="sm" 
                              className="me-2"
                              onClick={() => handleViewFile(file)}
                            >
                              <FaEye /> View
                            </Button>
                            <Button 
                              variant="outline-primary" 
                              size="sm" 
                              className="me-2"
                              onClick={() => handleDownloadFile(file)}
                            >
                              <FaDownload /> Download
                            </Button>
                            <Button 
                              variant="outline-danger" 
                              size="sm"
                              onClick={() => handleDeleteFile(file.id)}
                            >
                              <FaTrash />
                            </Button>
                          </div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}
                  
                  {/* Hidden file input for Files tab */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileInputChange}
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.gif,.xlsx,.xls,.ods,.msg,.eml"
                    style={{ display: 'none' }}
                  />
                </Card.Body>
              </Card>
            </Tab>
          </Tabs>
          

        </>
      ) : (
        <Alert variant="danger">Project not found</Alert>
      )}
      
      {/* File Upload Modal */}
      <Modal show={showUploadModal} onHide={() => {
        setShowUploadModal(false);
        setIsDragging(false);
        setSelectedFile(null);
        setSelectedFolderId(null);
      }} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {currentFolder ? `Upload PDF to ${currentFolder.name}` : 'Upload PDF'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleFileUpload}>
            <div
              ref={dropAreaRef}
              className={`drop-area mb-3 p-5 text-center border rounded ${isDragging ? 'dragging' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {selectedFile ? (
                <div>
                  <p>Selected file: {selectedFile.name}</p>
                  <Button variant="outline-secondary" onClick={() => setSelectedFile(null)}>
                    Change File
                  </Button>
                </div>
              ) : (
                <div>
                  <p>Drag & drop a file here, or click to select file</p>
                  <p className="text-muted small">Supported formats: PDF, JPG, PNG, GIF, Excel</p>
                  <Button variant="outline-primary" onClick={() => fileInputRef.current.click()}>
                    Select File
                  </Button>
                </div>
              )}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelection}
                className="d-none"
                accept=".pdf,.jpg,.jpeg,.png,.gif,.xlsx,.xls"
              />
            </div>
            
            <div className="text-end">
              <Button 
                variant="secondary" 
                className="me-2" 
                onClick={() => setShowUploadModal(false)}
              >
                Cancel
              </Button>
              <Button 
                variant="primary" 
                type="submit"
                disabled={uploading || !selectedFile}
              >
                {uploading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Uploading...
                  </>
                ) : (
                  'Upload'
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
      
      {/* File Viewer Modal */}
      <Modal 
        show={showFileViewerModal} 
        onHide={() => {
          setShowFileViewerModal(false);
          setSelectedFileForViewing(null);
        }}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedFileForViewing ? selectedFileForViewing.original_name : 'File Viewer'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ minHeight: '500px', maxHeight: '80vh', overflow: 'auto' }}>
          {selectedFileForViewing && (
            <FileViewer file={selectedFileForViewing} />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="primary" 
            onClick={() => handleDownloadFile(selectedFileForViewing)}
          >
            <FaDownload className="me-1" /> Download
          </Button>
          <Button 
            variant="secondary" 
            onClick={() => {
              setShowFileViewerModal(false);
              setSelectedFileForViewing(null);
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Create Door Modal with Metadata Form */}
      <Modal show={showFolderModal} onHide={() => setShowFolderModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Create New Door</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleCreateFolder}>
            <h5 className="mb-3">1. Basic Information</h5>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Door Type/Series <span className="text-danger">*</span></Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <FaDoorClosed />
                    </InputGroup.Text>
                    <Form.Control
                      as="select"
                      value={folderName}
                      onChange={(e) => {
                        const selectedType = e.target.value;
                        setFolderName(selectedType);
                        
                        // Auto-select threshold and waternose based on door type
                        let newThreshold = doorData.thresholdType;
                        let newWaternose = false;
                        
                        if (selectedType === 'MER' || selectedType === 'COT' || selectedType === '18D') {
                          // Meranti, Cottage, 18° -> Pine threshold + Waternose
                          newThreshold = 'Pine';
                          newWaternose = true;
                        } else if (selectedType === '60I') {
                          // 60mm Inswing -> Alu threshold + Waternose
                          newThreshold = 'Alu w holes';
                          newWaternose = true;
                        } else if (selectedType === '60O') {
                          // 60mm Outswing -> Alu threshold + No Waternose
                          newThreshold = 'Alu w holes';
                          newWaternose = false;
                        }
                        
                        setDoorData({...doorData, doorName: selectedType, thresholdType: newThreshold, waternose: newWaternose});
                      }}
                      required
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m1 6 7 7 7-7'/%3e%3c/svg%3e")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right .75rem center',
                        backgroundSize: '16px 12px'
                      }}
                    >
                      <option value="">Select Door Type</option>
                      {doorTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </Form.Control>
                  </InputGroup>
                  <Form.Text className="text-muted">
                    Select the door series/type for this door
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                {/* Door Preview */}
                {folderName && (
                  <div className="text-center">
                    <Form.Label>Door Preview</Form.Label>
                    <RenderWindow 
                      doorMetadata={{...doorData, doorName: folderName}}
                      size="medium"
                      className="mx-auto mb-2"
                    />
                    <small className="text-muted">{getDoorTypeLabel(folderName)}</small>
                  </div>
                )}
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Threshold</Form.Label>
                  <Form.Control
                    as="select"
                    value={doorData.thresholdType}
                    onChange={(e) => setDoorData({...doorData, thresholdType: e.target.value})}
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m1 6 7 7 7-7'/%3e%3c/svg%3e")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right .75rem center',
                      backgroundSize: '16px 12px'
                    }}
                  >
                    {thresholdTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Width (mm) <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="number"
                    step="0.1"
                    placeholder="Enter width"
                    value={doorData.width}
                    onChange={(e) => setDoorData({...doorData, width: e.target.value})}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Height (mm) <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="number"
                    step="0.1"
                    placeholder="Enter height"
                    value={doorData.height}
                    onChange={(e) => setDoorData({...doorData, height: e.target.value})}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Quantity</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    placeholder="Quantity"
                    value={doorData.amount}
                    onChange={(e) => setDoorData({...doorData, amount: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <h6 className="mb-3 mt-4">2. Hardware</h6>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Handedness</Form.Label>
                  <Form.Control
                    as="select"
                    value={doorData.handedness}
                    onChange={(e) => setDoorData({...doorData, handedness: e.target.value})}
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m1 6 7 7 7-7'/%3e%3c/svg%3e")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right .75rem center',
                      backgroundSize: '16px 12px'
                    }}
                  >
                    {handednessOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Hinge Count</Form.Label>
                  <Form.Control
                    as="select"
                    value={doorData.hingeCount}
                    onChange={(e) => setDoorData({...doorData, hingeCount: e.target.value})}
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m1 6 7 7 7-7'/%3e%3c/svg%3e")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right .75rem center',
                      backgroundSize: '16px 12px'
                    }}
                  >
                    {hingeCountOptions.map(count => (
                      <option key={count} value={count}>{count} Hinges</option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Hinge Type</Form.Label>
                  <Form.Control
                    as="select"
                    value={doorData.hingeType}
                    onChange={(e) => setDoorData({...doorData, hingeType: e.target.value})}
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m1 6 7 7 7-7'/%3e%3c/svg%3e")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right .75rem center',
                      backgroundSize: '16px 12px'
                    }}
                  >
                    {hingeTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Lock</Form.Label>
                  <Form.Control
                    as="select"
                    value={doorData.lockType}
                    onChange={(e) => setDoorData({...doorData, lockType: e.target.value})}
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m1 6 7 7 7-7'/%3e%3c/svg%3e")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right .75rem center',
                      backgroundSize: '16px 12px'
                    }}
                  >
                    {lockTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Lock</Form.Label>
                  <Form.Control
                    as="select"
                    value={doorData.lockType}
                    onChange={(e) => setDoorData({...doorData, lockType: e.target.value})}
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m1 6 7 7 7-7'/%3e%3c/svg%3e")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right .75rem center',
                      backgroundSize: '16px 12px'
                    }}
                  >
                    {lockTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Striker</Form.Label>
                  <Form.Control
                    as="select"
                    value={doorData.strikerType}
                    onChange={(e) => setDoorData({...doorData, strikerType: e.target.value})}
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m1 6 7 7 7-7'/%3e%3c/svg%3e")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right .75rem center',
                      backgroundSize: '16px 12px'
                    }}
                  >
                    {strikerTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>
            
            <h6 className="mb-3 mt-4">3. Frame</h6>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Frame Paint</Form.Label>
                  <Form.Control
                    as="select"
                    value={doorData.framePaint}
                    onChange={(e) => setDoorData({...doorData, framePaint: e.target.value})}
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m1 6 7 7 7-7'/%3e%3c/svg%3e")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right .75rem center',
                      backgroundSize: '16px 12px'
                    }}
                  >
                    {framePaintOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Active/Passive (Double Doors)</Form.Label>
                  <Form.Control
                    as="select"
                    value={doorData.doorStatus}
                    onChange={(e) => setDoorData({...doorData, doorStatus: e.target.value})}
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m1 6 7 7 7-7'/%3e%3c/svg%3e")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right .75rem center',
                      backgroundSize: '16px 12px'
                    }}
                  >
                    {doorStatusOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>
            
            {/* Glass Section */}
            <h6 className="mb-3 mt-4">4. Glass & Design</h6>
            <Row>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Glass Style</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    max="99"
                    placeholder="00-99"
                    value={doorData.glassStyle}
                    onChange={(e) => {
                      const value = e.target.value;
                      setDoorData({
                        ...doorData, 
                        glassStyle: value,
                        hasGlass: value !== '00' && value !== '0' && value !== ''
                      });
                    }}
                  />
                  <Form.Text className="text-muted">00 = none, 01-99 = style</Form.Text>
                </Form.Group>
              </Col>
              {doorData.hasGlass && (
                <>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Glass Width (mm)</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        max="9999"
                        placeholder="e.g. 500"
                        value={doorData.glassWidth}
                        onChange={(e) => setDoorData({...doorData, glassWidth: e.target.value})}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Glass Height (mm)</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        max="9999"
                        placeholder="e.g. 1000"
                        value={doorData.glassHeight}
                        onChange={(e) => setDoorData({...doorData, glassHeight: e.target.value})}
                      />
                    </Form.Group>
                  </Col>
                </>
              )}
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Groove Design</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    max="999"
                    placeholder="000-999"
                    value={doorData.grooveDesign}
                    onChange={(e) => setDoorData({...doorData, grooveDesign: e.target.value})}
                  />
                  <Form.Text className="text-muted">000 = none</Form.Text>
                </Form.Group>
              </Col>
            </Row>
            
            {/* Surface Finish Section */}
            <h6 className="mb-3 mt-4">5. Surface Finish</h6>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Paint Type</Form.Label>
                  <Form.Control
                    as="select"
                    value={doorData.surfaceFinish}
                    onChange={(e) => setDoorData({...doorData, surfaceFinish: e.target.value})}
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m1 6 7 7 7-7'/%3e%3c/svg%3e")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right .75rem center',
                      backgroundSize: '16px 12px'
                    }}
                  >
                    {surfaceFinishes.map(finish => (
                      <option key={finish.value} value={finish.value}>{finish.label}</option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    {doorData.surfaceFinish === 'R' ? 'RAL Code' : 
                     doorData.surfaceFinish === 'N' ? 'NCS Code' : 'Stain Code'}
                  </Form.Label>
                  <div className="d-flex align-items-center">
                    <Form.Control
                      type="text"
                      placeholder={
                        doorData.surfaceFinish === 'R' ? 'e.g. 7016' :
                        doorData.surfaceFinish === 'N' ? 'e.g. S0502Y' :
                        'e.g. WAL'
                      }
                      value={doorData.colorCode}
                      onChange={(e) => setDoorData({...doorData, colorCode: e.target.value.toUpperCase()})}
                    />
                    {doorData.colorCode && (doorData.surfaceFinish === 'R' || doorData.surfaceFinish === 'N') && (
                      <div
                        className="ms-2 border rounded"
                        style={{
                          width: '30px',
                          height: '30px',
                          backgroundColor: getColorPreview(doorData.surfaceFinish === 'R' ? 'RAL' : 'NCS', doorData.colorCode),
                          minWidth: '30px'
                        }}
                        title={`Color preview: ${doorData.colorCode}`}
                      />
                    )}
                  </div>
                  <Form.Text className="text-muted">
                    {doorData.surfaceFinish === 'R' && 'RAL code without prefix (e.g., 7016)'}
                    {doorData.surfaceFinish === 'N' && 'NCS code without hyphens (e.g., S0502Y)'}
                    {doorData.surfaceFinish === 'ST' && 'Stain code (e.g., WAL for walnut)'}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            
            {/* Additional Options */}
            <h6 className="mb-3 mt-4">6. Additional Options</h6>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Waternose"
                    checked={doorData.waternose}
                    onChange={(e) => setDoorData({...doorData, waternose: e.target.checked})}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Kickplate"
                    checked={doorData.kickplate}
                    onChange={(e) => setDoorData({...doorData, kickplate: e.target.checked})}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Special Feature (adds X marker)"
                    checked={doorData.specialFeature}
                    onChange={(e) => setDoorData({...doorData, specialFeature: e.target.checked})}
                  />
                  <Form.Text className="text-muted">
                    Check if door has special features
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            
            <div className="d-flex justify-content-end mt-3">
              <Button 
                variant="secondary" 
                className="me-2" 
                onClick={() => setShowFolderModal(false)}
              >
                Cancel
              </Button>
              <Button 
                variant="primary" 
                type="submit"
              >
                Create Door
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
      
      {/* Door Data Form Modal */}
      <Modal 
        show={showDoorForm} 
        onHide={() => {
          setShowDoorForm(false);
          setSelectedFolder(null);
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedFolder ? `Door Data - ${selectedFolder.name}` : 'Door Data'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedFolder && (
            <Form onSubmit={handleDoorDataSubmit}>
              {/* Door Preview Section */}
              <div className="text-center mb-4">
                <h6>Door Preview</h6>
                <RenderWindow 
                  doorMetadata={doorData}
                  size="large"
                  showDetails={true}
                  className="mx-auto"
                />
              </div>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Door Width (mm)</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.1"
                      value={doorData.width}
                      onChange={(e) => setDoorData({...doorData, width: e.target.value})}
                      placeholder="Enter width"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Door Height (mm)</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.1"
                      value={doorData.height}
                      onChange={(e) => setDoorData({...doorData, height: e.target.value})}
                      placeholder="Enter height"
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Handedness</Form.Label>
                    <Form.Control
                      as="select"
                      value={doorData.handedness}
                      onChange={(e) => setDoorData({...doorData, handedness: e.target.value})}
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m1 6 7 7 7-7'/%3e%3c/svg%3e")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right .75rem center',
                        backgroundSize: '16px 12px'
                      }}
                    >
                      {handednessOptions.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Quantity</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      value={doorData.amount}
                      onChange={(e) => setDoorData({...doorData, amount: parseInt(e.target.value) || 1})}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Form.Group className="mb-3">
                <Form.Label>Door Type/Series</Form.Label>
                <Form.Control
                  as="select"
                  value={doorData.doorName}
                  onChange={(e) => {
                    const selectedType = e.target.value;
                    
                    // Auto-select threshold and waternose based on door type
                    let newThreshold = doorData.thresholdType;
                    let newWaternose = doorData.waternose;
                    
                    if (selectedType === 'MER' || selectedType === 'COT' || selectedType === '18D') {
                      // Meranti, Cottage, 18° -> Pine threshold + Waternose
                      newThreshold = 'Pine';
                      newWaternose = true;
                    } else if (selectedType === '60I') {
                      // 60mm Inswing -> Alu threshold + Waternose
                      newThreshold = 'Alu w holes';
                      newWaternose = true;
                    } else if (selectedType === '60O') {
                      // 60mm Outswing -> Alu threshold + No Waternose
                      newThreshold = 'Alu w holes';
                      newWaternose = false;
                    }
                    
                    setDoorData({...doorData, doorName: selectedType, thresholdType: newThreshold, waternose: newWaternose});
                  }}
                  required
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m1 6 7 7 7-7'/%3e%3c/svg%3e")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right .75rem center',
                    backgroundSize: '16px 12px'
                  }}
                >
                  {doorTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </Form.Control>
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Threshold</Form.Label>
                <Form.Control
                  as="select"
                  value={doorData.thresholdType}
                  onChange={(e) => setDoorData({...doorData, thresholdType: e.target.value})}
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m1 6 7 7 7-7'/%3e%3c/svg%3e")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right .75rem center',
                    backgroundSize: '16px 12px'
                  }}
                >
                  {thresholdTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </Form.Control>
              </Form.Group>
              
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Hinge</Form.Label>
                    <Form.Control
                      as="select"
                      value={doorData.hingeType}
                      onChange={(e) => setDoorData({...doorData, hingeType: e.target.value})}
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m1 6 7 7 7-7'/%3e%3c/svg%3e")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right .75rem center',
                        backgroundSize: '16px 12px'
                      }}
                    >
                      {hingeTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Hinge Count</Form.Label>
                    <Form.Control
                      as="select"
                      value={doorData.hingeCount}
                      onChange={(e) => setDoorData({...doorData, hingeCount: e.target.value})}
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m1 6 7 7 7-7'/%3e%3c/svg%3e")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right .75rem center',
                        backgroundSize: '16px 12px'
                      }}
                    >
                      {hingeCountOptions.map(count => (
                        <option key={count} value={count}>{count} Hinges</option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Lock</Form.Label>
                    <Form.Control
                      as="select"
                      value={doorData.lockType}
                      onChange={(e) => setDoorData({...doorData, lockType: e.target.value})}
                      required
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m1 6 7 7 7-7'/%3e%3c/svg%3e")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right .75rem center',
                        backgroundSize: '16px 12px'
                      }}
                    >
                      {lockTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Striker</Form.Label>
                    <Form.Control
                      as="select"
                      value={doorData.strikerType}
                      onChange={(e) => setDoorData({...doorData, strikerType: e.target.value})}
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m1 6 7 7 7-7'/%3e%3c/svg%3e")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right .75rem center',
                        backgroundSize: '16px 12px'
                      }}
                    >
                      {strikerTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Frame Paint</Form.Label>
                    <Form.Control
                      as="select"
                      value={doorData.framePaint}
                      onChange={(e) => setDoorData({...doorData, framePaint: e.target.value})}
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m1 6 7 7 7-7'/%3e%3c/svg%3e")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right .75rem center',
                        backgroundSize: '16px 12px'
                      }}
                    >
                      {framePaintOptions.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Active/Passive (Double Doors)</Form.Label>
                    <Form.Control
                      as="select"
                      value={doorData.doorStatus}
                      onChange={(e) => setDoorData({...doorData, doorStatus: e.target.value})}
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m1 6 7 7 7-7'/%3e%3c/svg%3e")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right .75rem center',
                        backgroundSize: '16px 12px'
                      }}
                    >
                      {doorStatusOptions.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                </Col>
              </Row>
              
              {/* Glass Section */}
              <h6 className="mb-3 mt-4">Glass & Design</h6>
              <Row>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Glass Style</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      max="99"
                      placeholder="00-99"
                      value={doorData.glassStyle}
                      onChange={(e) => {
                        const value = e.target.value;
                        setDoorData({
                          ...doorData, 
                          glassStyle: value,
                          hasGlass: value !== '00' && value !== '0' && value !== ''
                        });
                      }}
                    />
                    <Form.Text className="text-muted">00 = none, 01-99 = style</Form.Text>
                  </Form.Group>
                </Col>
                {doorData.hasGlass && (
                  <>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Glass Width (mm)</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          max="9999"
                          placeholder="e.g. 500"
                          value={doorData.glassWidth}
                          onChange={(e) => setDoorData({...doorData, glassWidth: e.target.value})}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Glass Height (mm)</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          max="9999"
                          placeholder="e.g. 1000"
                          value={doorData.glassHeight}
                          onChange={(e) => setDoorData({...doorData, glassHeight: e.target.value})}
                        />
                      </Form.Group>
                    </Col>
                  </>
                )}
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Groove Design</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      max="999"
                      placeholder="000-999"
                      value={doorData.grooveDesign}
                      onChange={(e) => setDoorData({...doorData, grooveDesign: e.target.value})}
                    />
                    <Form.Text className="text-muted">000 = none</Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              
              {/* Surface Finish Section */}
              <h6 className="mb-3 mt-4">Surface Finish</h6>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Surface Finish</Form.Label>
                    <Form.Control
                      as="select"
                      value={doorData.surfaceFinish}
                      onChange={(e) => setDoorData({...doorData, surfaceFinish: e.target.value})}
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m1 6 7 7 7-7'/%3e%3c/svg%3e")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right .75rem center',
                        backgroundSize: '16px 12px'
                      }}
                    >
                      {surfaceFinishes.map(finish => (
                        <option key={finish.value} value={finish.value}>{finish.label}</option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    {doorData.surfaceFinish === 'R' ? 'RAL Code' : 
                     doorData.surfaceFinish === 'N' ? 'NCS Code' : 'Stain Code'}
                  </Form.Label>
                  <div className="d-flex align-items-center">
                    <Form.Control
                      type="text"
                      placeholder={
                        doorData.surfaceFinish === 'R' ? 'e.g. 7016' :
                        doorData.surfaceFinish === 'N' ? 'e.g. S0502Y' :
                        'e.g. WAL'
                      }
                      value={doorData.colorCode}
                      onChange={(e) => setDoorData({...doorData, colorCode: e.target.value.toUpperCase()})}
                    />
                    {doorData.colorCode && (doorData.surfaceFinish === 'R' || doorData.surfaceFinish === 'N') && (
                      <div
                        className="ms-2 border rounded"
                        style={{
                          width: '30px',
                          height: '30px',
                          backgroundColor: getColorPreview(doorData.surfaceFinish === 'R' ? 'RAL' : 'NCS', doorData.colorCode),
                          minWidth: '30px'
                        }}
                        title={`Color preview: ${doorData.colorCode}`}
                      />
                    )}
                  </div>
                  <Form.Text className="text-muted">
                    {doorData.surfaceFinish === 'R' && 'RAL code without prefix (e.g., 7016)'}
                    {doorData.surfaceFinish === 'N' && 'NCS code without hyphens (e.g., S0502Y)'}
                    {doorData.surfaceFinish === 'ST' && 'Stain code (e.g., WAL for walnut)'}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

              {/* Additional Options */}
              <h6 className="mb-3 mt-4">Additional Options</h6>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="Waternose"
                      checked={doorData.waternose}
                      onChange={(e) => setDoorData({...doorData, waternose: e.target.checked})}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="Kickplate"
                      checked={doorData.kickplate}
                      onChange={(e) => setDoorData({...doorData, kickplate: e.target.checked})}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="Special Feature (adds X marker)"
                      checked={doorData.specialFeature}
                      onChange={(e) => setDoorData({...doorData, specialFeature: e.target.checked})}
                    />
                    <Form.Text className="text-muted">
                      Check if door has special features
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              
              <div className="d-flex justify-content-end">
                <Button 
                  variant="secondary" 
                  className="me-2" 
                  onClick={() => {
                    setShowDoorForm(false);
                    setSelectedFolder(null);
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  variant="primary" 
                  type="submit"
                >
                  Save Door Data
                </Button>
              </div>
            </Form>
          )}
        </Modal.Body>
      </Modal>

      {/* PDF Upload Modal */}
      <Modal show={showPdfUploadModal} onHide={() => setShowPdfUploadModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Upload PDF Drawings</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div 
            ref={dropAreaRef}
            className={`upload-area p-4 border-2 border-dashed rounded text-center ${isDragging ? 'border-primary bg-light' : 'border-secondary'}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <FaUpload size={48} className="text-muted mb-3" />
            <p className="mb-3">Drag and drop PDF files here, or click to select</p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <Button variant="outline-primary" onClick={() => fileInputRef.current?.click()}>
              Select Files
            </Button>
          </div>
          
          {selectedFile && (
            <div className="mt-3">
              <p><strong>Selected:</strong> {selectedFile.name}</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPdfUploadModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleUpload}
            disabled={!selectedFile}
          >
            Upload
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Project Edit Modal */}
      <Modal show={showProjectEditModal} onHide={() => setShowProjectEditModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Project</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleEditProject}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Project Name</Form.Label>
                  <Form.Control 
                    type="text" 
                    value={editProjectData.name}
                    onChange={(e) => setEditProjectData({...editProjectData, name: e.target.value})}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Control
                    as="select"
                    value={editProjectData.status}
                    onChange={(e) => setEditProjectData({...editProjectData, status: e.target.value})}
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m1 6 7 7 7-7'/%3e%3c/svg%3e")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right .75rem center',
                      backgroundSize: '16px 12px'
                    }}
                  >
                    <option value="Draft">Draft</option>
                    <option value="Released for Production">Released for Production</option>
                    <option value="In Production">In Production</option>
                    <option value="Completed">Completed</option>
                    <option value="Delivered">Delivered</option>
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control 
                as="textarea" 
                rows={3}
                value={editProjectData.description}
                onChange={(e) => setEditProjectData({...editProjectData, description: e.target.value})}
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Client</Form.Label>
                  <Form.Control
                    as="select"
                    value={editProjectData.clientId || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      console.log('Client selected:', value);
                      setEditProjectData({
                        ...editProjectData, 
                        clientId: value === '' ? null : value
                      });
                    }}
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m1 6 7 7 7-7'/%3e%3c/svg%3e")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right .75rem center',
                      backgroundSize: '16px 12px'
                    }}
                  >
                    <option value="">No Client</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.name}{client.company_name ? ` (${client.company_name})` : ''}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Due Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={editProjectData.dueDate}
                    onChange={(e) => setEditProjectData({...editProjectData, dueDate: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <div className="d-flex justify-content-end">
              <Button 
                variant="secondary" 
                onClick={() => setShowProjectEditModal(false)} 
                className="me-2"
              >
                Cancel
              </Button>
              <Button 
                variant="primary" 
                type="submit"
              >
                Update Project
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Copy Door Modal */}
      <Modal show={showCopyModal} onHide={() => setShowCopyModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Copy Door</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            You are about to copy the door "<strong>{doorToCopy?.name}</strong>" 
            {doorToCopy?.doorMetadata && (
              <span> ({doorToCopy.doorMetadata.width}×{doorToCopy.doorMetadata.height}mm)</span>
            )}.
          </p>
          <p>The door will be copied with the same name and all specifications to the selected project.</p>
          
          <Form.Group className="mb-3">
            <Form.Label>Copy to Project</Form.Label>
            <Form.Select
              value={copyToProjectId}
              onChange={(e) => setCopyToProjectId(e.target.value)}
            >
              <option value="">Select a project...</option>
              {availableProjects.map(proj => (
                <option key={proj.id} value={proj.id}>
                  {proj.project_number} - {proj.name}
                  {proj.id === id && ' (Current Project)'}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCopyModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={executeCopyDoor}
            disabled={!copyToProjectId}
          >
            <FaCopy className="me-1" /> Copy Door
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Door Details Modal */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaInfoCircle className="me-2" />
            Door Raw Data - {selectedDoorForDetails?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedDoorForDetails && (
            <div>
              <Tabs defaultActiveKey="folder" className="mb-3">
                <Tab eventKey="folder" title="Folder Data">
                  <Card>
                    <Card.Header>
                      <strong>Folder Information</strong>
                    </Card.Header>
                    <Card.Body>
                      <pre style={{ fontSize: '0.85rem', backgroundColor: '#f8f9fa', padding: '1rem', borderRadius: '0.375rem', overflow: 'auto' }}>
                        {JSON.stringify({
                          id: selectedDoorForDetails.id,
                          name: selectedDoorForDetails.name,
                          folder_type: selectedDoorForDetails.folder_type,
                          project_id: selectedDoorForDetails.project_id,
                          created_at: selectedDoorForDetails.created_at,
                          updated_at: selectedDoorForDetails.updated_at,
                          created_by: selectedDoorForDetails.created_by
                        }, null, 2)}
                      </pre>
                    </Card.Body>
                  </Card>
                </Tab>
                
                <Tab eventKey="metadata" title="Door Metadata">
                  <Card>
                    <Card.Header>
                      <strong>Door Specifications</strong>
                    </Card.Header>
                    <Card.Body>
                      {selectedDoorForDetails.doorMetadata ? (
                        <pre style={{ fontSize: '0.85rem', backgroundColor: '#f8f9fa', padding: '1rem', borderRadius: '0.375rem', overflow: 'auto' }}>
                          {JSON.stringify(selectedDoorForDetails.doorMetadata, null, 2)}
                        </pre>
                      ) : (
                        <Alert variant="info">No door metadata available</Alert>
                      )}
                    </Card.Body>
                  </Card>
                </Tab>

                <Tab eventKey="files" title="Files">
                  <Card>
                    <Card.Header>
                      <strong>Associated Files</strong>
                    </Card.Header>
                    <Card.Body>
                      {selectedDoorForDetails.files && selectedDoorForDetails.files.length > 0 ? (
                        <pre style={{ fontSize: '0.85rem', backgroundColor: '#f8f9fa', padding: '1rem', borderRadius: '0.375rem', overflow: 'auto' }}>
                          {JSON.stringify(selectedDoorForDetails.files, null, 2)}
                        </pre>
                      ) : (
                        <Alert variant="info">No files associated with this door</Alert>
                      )}
                    </Card.Body>
                  </Card>
                </Tab>

                <Tab eventKey="raw" title="Complete Raw Data">
                  <Card>
                    <Card.Header>
                      <strong>Complete Object (All Data)</strong>
                    </Card.Header>
                    <Card.Body>
                      <pre style={{ fontSize: '0.75rem', backgroundColor: '#f8f9fa', padding: '1rem', borderRadius: '0.375rem', overflow: 'auto', maxHeight: '400px' }}>
                        {JSON.stringify(selectedDoorForDetails, null, 2)}
                      </pre>
                    </Card.Body>
                  </Card>
                </Tab>
              </Tabs>

              {/* Quick Copy UUIDs */}
              <Row className="mt-3">
                <Col md={6}>
                  <Card className="h-100">
                    <Card.Header className="bg-primary text-white">
                      <strong>Quick Copy - UUIDs</strong>
                    </Card.Header>
                    <Card.Body>
                      <div className="mb-2">
                        <strong>Folder ID:</strong>
                        <InputGroup size="sm" className="mt-1">
                          <Form.Control
                            value={selectedDoorForDetails.id || ''}
                            readOnly
                            style={{ fontSize: '0.75rem' }}
                          />
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => {
                              navigator.clipboard.writeText(selectedDoorForDetails.id);
                              toast.success('Folder ID copied!');
                            }}
                          >
                            Copy
                          </Button>
                        </InputGroup>
                      </div>
                      
                      {selectedDoorForDetails.doorMetadata?.id && (
                        <div className="mb-2">
                          <strong>Metadata ID:</strong>
                          <InputGroup size="sm" className="mt-1">
                            <Form.Control
                              value={selectedDoorForDetails.doorMetadata.id}
                              readOnly
                              style={{ fontSize: '0.75rem' }}
                            />
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => {
                                navigator.clipboard.writeText(selectedDoorForDetails.doorMetadata.id);
                                toast.success('Metadata ID copied!');
                              }}
                            >
                              Copy
                            </Button>
                          </InputGroup>
                        </div>
                      )}

                      <div>
                        <strong>Project ID:</strong>
                        <InputGroup size="sm" className="mt-1">
                          <Form.Control
                            value={selectedDoorForDetails.project_id || ''}
                            readOnly
                            style={{ fontSize: '0.75rem' }}
                          />
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => {
                              navigator.clipboard.writeText(selectedDoorForDetails.project_id);
                              toast.success('Project ID copied!');
                            }}
                          >
                            Copy
                          </Button>
                        </InputGroup>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                
                <Col md={6}>
                  <Card className="h-100">
                    <Card.Header className="bg-info text-white">
                      <strong>Database Info</strong>
                    </Card.Header>
                    <Card.Body>
                      <div className="mb-2">
                        <strong>Table:</strong> <code>folders</code>
                      </div>
                      <div className="mb-2">
                        <strong>Created:</strong> {selectedDoorForDetails.created_at ? new Date(selectedDoorForDetails.created_at).toLocaleString() : 'N/A'}
                      </div>
                      <div className="mb-2">
                        <strong>Updated:</strong> {selectedDoorForDetails.updated_at ? new Date(selectedDoorForDetails.updated_at).toLocaleString() : 'N/A'}
                      </div>
                      <div className="mb-2">
                        <strong>Files Count:</strong> {selectedDoorForDetails.files?.length || 0}
                      </div>
                      <div>
                        <strong>Has Metadata:</strong> {selectedDoorForDetails.doorMetadata ? '✅ Yes' : '❌ No'}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Drawing Generator Modal */}
      <DrawingGeneratorModal
        show={showDrawingGeneratorModal}
        onHide={() => {
          setShowDrawingGeneratorModal(false);
          setSelectedDoorForDrawing(null);
        }}
        doorMetadata={selectedDoorForDrawing?.doorMetadata}
        projectInfo={project}
        folderId={selectedDoorForDrawing?.id}
        onPdfSaved={handlePdfSaved}
        userProfile={profile}
      />

      {/* Similar Doors Modal */}
      <Modal 
        show={showSimilarDoorsModal} 
        onHide={() => setShowSimilarDoorsModal(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaInfoCircle className="me-2" />
            Similar Doors Across Projects
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingSimilarDoors ? (
            <div className="text-center py-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <p className="mt-3">Searching for similar doors...</p>
            </div>
          ) : similarDoors.length === 0 ? (
            <Alert variant="info">
              <FaInfoCircle className="me-2" />
              No other doors found with the same specifications.
            </Alert>
          ) : (
            <div>
              <Alert variant="success">
                <strong>Found {similarDoors.length} door(s)</strong> with the same item number across different projects.
              </Alert>
              
              <ListGroup>
                {similarDoors.map((door, index) => (
                  <ListGroup.Item key={index} className="mb-2">
                    <Row>
                      <Col md={8}>
                        <h6 className="mb-2">
                          <FaDoorClosed className="me-2" />
                          {door.folderName}
                        </h6>
                        <div className="mb-2">
                          <strong>Project:</strong>{' '}
                          <Link 
                            to={`/admin/project/${door.projectId}`}
                            onClick={() => setShowSimilarDoorsModal(false)}
                          >
                            {door.projectName}
                          </Link>
                          {' '}
                          <span className={`badge bg-${
                            door.projectStatus === 'Completed' ? 'success' :
                            door.projectStatus === 'In Progress' ? 'primary' :
                            door.projectStatus === 'On Hold' ? 'warning' : 'secondary'
                          }`}>
                            {door.projectStatus}
                          </span>
                        </div>
                        <div className="mb-2">
                          <strong>Item Number:</strong>{' '}
                          <code className="bg-light px-2 py-1 rounded">{door.itemNr}</code>
                        </div>
                        <div className="mb-2">
                          <strong>Dimensions:</strong> {door.width} × {door.height} mm
                        </div>
                        <div className="mb-2">
                          <strong>Quantity:</strong> {door.amount}
                        </div>
                        <div className="text-muted small">
                          <strong>Created:</strong> {new Date(door.createdAt).toLocaleString()}
                        </div>
                      </Col>
                      <Col md={4} className="text-center">
                        {door.barcodeUrl && (
                          <div>
                            <img 
                              src={door.barcodeUrl} 
                              alt="Barcode"
                              style={{ 
                                maxWidth: '100%', 
                                height: 'auto', 
                                border: '1px solid #dee2e6', 
                                borderRadius: '4px', 
                                padding: '4px',
                                background: 'white'
                              }}
                            />
                          </div>
                        )}
                      </Col>
                    </Row>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSimilarDoorsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ProjectDetails;
